import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, Users, MapPin,
  Clock, Zap, Award,
  Brain, Briefcase, Loader2, Trash2,
  Edit3, PauseCircle, PlayCircle, RefreshCw,
  Building2, DollarSign, GraduationCap, Lock, ShieldCheck,
  AlertTriangle, X, Camera, Navigation, Video, Link2,
  CheckCircle2, ExternalLink, ImagePlus, Trash, Check,
  ChevronDown, FileText, FileJson, FileSpreadsheet, Github,
  Sparkles, Bot, Code2, Link, BookOpen, FlaskConical,
  GitCommit, GitBranch, CalendarDays, ChevronRight,
  Star, Send, MessageSquare, ThumbsUp,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  listMyPostings, createPosting, updatePosting, deletePosting,
  listApplications, updateApplicationStage,
  getMyStartup, registerOfflineCenter, uploadPhoto,
  getNsqfPublicGroups, getNsqfPublicCourses,
  getApplicationDetail, getApplicantResume, requestCandidateAccess, getAccessResume, triggerAiEvaluation, requestAiEvalConsent, getAiEvalConsentStatus,
  getRecommendedCandidates, expressCandidateInterest, submitCandidateFeedback,
  getApplicationMatchScores,
} from '@/api/stnApi';
import InternProgressPanel from '@/components/interns/InternProgressPanel';

// ── Constants ─────────────────────────────────────────────────────────────────

const DOMAINS   = ['AI/ML', 'Web Dev', 'Data Analytics', 'DevOps', 'Design', 'Marketing', 'Finance', 'HR', 'Operations', 'Other'];
const DURATIONS = ['1 month', '2 months', '3 months', '4 months', '6 months', '1 year'];
const STAGES    = ['applied', 'screening', 'interview', 'hired', 'rejected'];
const STAGE_LABELS = { applied: 'Applied', screening: 'Screening', interview: 'Interview', hired: 'Hired', rejected: 'Rejected' };
const STAGE_COLORS = {
  applied:   'bg-blue-50 text-blue-700 border-blue-200',
  screening: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  interview: 'bg-purple-50 text-purple-700 border-purple-200',
  hired:     'bg-green-50 text-green-700 border-green-200',
  rejected:  'bg-red-50 text-red-700 border-red-200',
};

const EMPTY_FORM = {
  title: '', domain: '', workType: 'remote', location: '',
  description: '', responsibilities: [], requirements: [], perks: [],
  skills: [], duration: '3 months', startDate: '', applicationDeadline: '',
  stipend: '', isPaid: false, payType: 'free', payMonths: '', payHours: '', openings: 1,
  minCgpa: 0, branches: [], yearOfStudy: [],
  ppoOffered: false, certificateOffered: true,
  status: 'draft',
  // POC / Assignment
  pocRequired: false,
  pocFormats: [],
  pocTaskTitle: '',
  pocTaskDescription: '',
  pocSampleCode: '',
  pocReferenceLinks: [],
  // AI / Prompt Engineer section
  isAiRole: false,
  aiTools: [],
  aiSpecializations: [],
  llmFrameworks: [],
  promptEngLevel: '',
};

// ── AI role constants ─────────────────────────────────────────────────────────

const AI_TOOLS = [
  'ChatGPT / GPT-4o', 'Claude (Anthropic)', 'Gemini (Google)', 'Copilot (GitHub)',
  'Cursor AI', 'v0 (Vercel)', 'Perplexity AI', 'Midjourney', 'DALL-E 3',
  'Stable Diffusion', 'ElevenLabs', 'RunwayML', 'Sora', 'Kling AI',
  'Hugging Face', 'Replicate', 'Together AI', 'Groq', 'Ollama',
];

const AI_SPECIALIZATIONS = [
  'Prompt Engineering', 'RAG (Retrieval-Augmented Generation)', 'Fine-tuning / LoRA',
  'AI Agents / Agentic Workflows', 'Multi-modal AI', 'Voice AI (TTS / STT)',
  'AI Image Generation', 'AI Video Generation', 'LLM Evaluation & Red-teaming',
  'Function Calling / Tool Use', 'Embeddings & Vector Search', 'AI Safety & Alignment',
  'Synthetic Data Generation', 'Code Generation', 'Document Intelligence',
];

const LLM_FRAMEWORKS = [
  'LangChain', 'LangGraph', 'LlamaIndex', 'CrewAI', 'AutoGen', 'DSPy',
  'Haystack', 'Semantic Kernel', 'Instructor', 'Outlines', 'LiteLLM',
  'Vercel AI SDK', 'OpenAI SDK', 'Anthropic SDK', 'Google GenAI SDK',
];

const PROMPT_ENG_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

const POC_FORMATS = [
  { id: 'pdf',    label: 'PDF',          icon: FileText,        accept: '.pdf' },
  { id: 'word',   label: 'Word (.docx)', icon: FileText,        accept: '.doc,.docx' },
  { id: 'excel',  label: 'Excel (.xlsx)',icon: FileSpreadsheet, accept: '.xls,.xlsx,.csv' },
  { id: 'json',   label: 'JSON',         icon: FileJson,        accept: '.json' },
  { id: 'github', label: 'GitHub Repo',  icon: Github,          accept: null },
  { id: 'code',   label: 'Code file',    icon: Code2,           accept: '.py,.js,.ts,.ipynb,.zip' },
];

// ── Pincode lookup — http://api.postalpincode.in/pincode/{pin} ────────────────

async function lookupPincode(pin) {
  const res  = await fetch(`/pincode-api/pincode/${pin}`);
  if (!res.ok) throw new Error('Network error');
  const json = await res.json();
  // Response: [{ Status, Message, PostOffice: [{ Name, District, State, Block, Pincode, ... }] }]
  const root = json?.[0];
  if (!root || root.Status !== 'Success' || !root.PostOffice?.length) return null;
  // Deduplicate and pick best district name for the city field
  const po       = root.PostOffice[0];
  const city     = po.District  !== 'NA' ? po.District  : (po.Block !== 'NA' ? po.Block : '');
  const state    = po.State     !== 'NA' ? po.State     : '';
  // Return all unique post offices for reference
  const offices  = [...new Map(root.PostOffice.map(p => [p.Name, p])).values()];
  return { city, state, offices };
}

// ── Register / Review Offline Center Dialog ───────────────────────────────────

function RegisterCenterDialog({ open, onClose, onRegistered, offlineCenters = [], forceEdit = false }) {
  const EMPTY = {
    address: '', city: '', state: '', pincode: '',
    videoUrl: '', mapLink: '', coordinates: null,
  };

  const [form, setForm]           = useState(EMPTY);
  const [pinStatus, setPinStatus] = useState('idle');
  const [postOffices, setPostOffices] = useState([]);
  const [photos, setPhotos]       = useState([]);
  const [geoStatus, setGeoStatus] = useState('idle');
  const [saving, setSaving]       = useState(false);
  // mode: 'list' (view all centers) | 'edit' (add/update form)
  const [mode, setMode]           = useState('list');
  // editingCenter: null = adding brand-new | center object = editing existing
  const [editingCenter, setEditingCenter] = useState(null);
  const fileInputRef              = useRef(null);

  const resetEditForm = (c = null) => {
    setForm(c ? {
      address:     c.address     || '',
      city:        c.city        || '',
      state:       c.state       || '',
      pincode:     c.pincode     || '',
      videoUrl:    c.videoUrl    || '',
      mapLink:     c.mapLink     || '',
      coordinates: c.coordinates || null,
    } : EMPTY);
    setPhotos(c ? (c.photoUrls || []).map(url => ({ preview: url, url, uploading: false })) : []);
    setPinStatus('idle');
    setPostOffices([]);
    setGeoStatus('idle');
  };

  useEffect(() => {
    if (!open) return;
    if (offlineCenters.length === 0 || forceEdit) {
      // No centers yet, or explicitly opening to add new → go straight to edit with empty form
      setEditingCenter(null);
      resetEditForm(null);
      setMode('edit');
    } else {
      setMode('list');
    }
  }, [open, forceEdit, offlineCenters.length]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Pincode → city + state auto-fill ──────────────────────────────────────
  const handlePincodeChange = async (raw) => {
    const val = raw.replace(/\D/g, '').slice(0, 6);
    set('pincode', val);
    if (val.length < 6) { setPinStatus('idle'); setPostOffices([]); return; }
    setPinStatus('loading');
    try {
      const result = await lookupPincode(val);
      if (result) {
        setForm(p => ({ ...p, city: result.city, state: result.state, pincode: val }));
        setPostOffices(result.offices || []);
        setPinStatus('found');
      } else {
        setPinStatus('error');
        setPostOffices([]);
      }
    } catch {
      setPinStatus('error');
    }
  };

  // ── Geolocation → Google Maps link ────────────────────────────────────────
  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation is not supported by your browser'); return; }
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const link = `https://www.google.com/maps?q=${lat},${lng}`;
        setForm(p => ({ ...p, mapLink: link, coordinates: { lat, lng } }));
        setGeoStatus('done');
        toast.success('Location detected and map link generated!');
      },
      (err) => {
        setGeoStatus('denied');
        if (err.code === 1) toast.error('Location access denied. Please allow location or paste the map link manually.');
        else toast.error('Could not detect location. Please paste your Google Maps link manually.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ── Camera capture with geolocation tagging ───────────────────────────────
  const handleAddPhoto = () => {
    if (photos.length >= 5) { toast.error('Maximum 5 photos allowed'); return; }
    // Request location first, then open camera
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          openCamera(lat, lng);
        },
        () => openCamera(null, null), // Location denied — still allow photo
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      openCamera(null, null);
    }
  };

  const openCamera = (lat, lng) => {
    // Store pending geo in a ref for the input handler
    if (fileInputRef.current) {
      fileInputRef.current._pendingGeo = { lat, lng };
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const geo = fileInputRef.current?._pendingGeo || {};
    const preview = URL.createObjectURL(file);
    const id = Date.now();
    // Add placeholder
    setPhotos(prev => [...prev, { id, preview, url: null, lat: geo.lat, lng: geo.lng, uploading: true }]);
    // Reset input so same file can be re-selected
    e.target.value = '';
    try {
      const res = await uploadPhoto(file);
      const url = res?.url || res?.Location || res?.photoUrl || '';
      setPhotos(prev => prev.map(p => p.id === id ? { ...p, url, uploading: false } : p));
    } catch {
      toast.error('Photo upload failed');
      setPhotos(prev => prev.filter(p => p.id !== id));
    }
  };

  const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.city.trim()) { toast.error('City is required'); return; }
    if (photos.some(p => p.uploading)) { toast.error('Please wait for photos to finish uploading'); return; }
    try {
      setSaving(true);
      const body = {
        ...form,
        photoUrls: photos.filter(p => p.url).map(p => p.url),
        centerId:  editingCenter?._id || undefined,
      };
      const res     = await registerOfflineCenter(body);
      const centers = res?.offlineCenters ?? (res ? [res] : []);
      toast.success(editingCenter ? 'Center updated! Awaiting admin approval.' : 'New center submitted! Awaiting admin approval.');
      onRegistered(centers);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to register center');
    } finally {
      setSaving(false);
    }
  };

  // ── List mode — all centers ───────────────────────────────────────────────
  const renderList = () => (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {offlineCenters.map((c) => {
        const isApproved = c.isApproved;
        const isRejected = !!c.rejectedAt && !c.isApproved;
        return (
          <div key={c._id} className={cn(
            'rounded-xl border p-4 space-y-2',
            isApproved ? 'border-green-200 bg-green-50/40'
              : isRejected ? 'border-red-200 bg-red-50/30'
              : 'border-amber-200 bg-amber-50/30'
          )}>
            {/* Status row */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {isApproved
                  ? <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                  : isRejected
                    ? <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                    : <Loader2 className="w-4 h-4 text-amber-500 animate-spin flex-shrink-0" />
                }
                <span className={cn('text-xs font-semibold',
                  isApproved ? 'text-green-700' : isRejected ? 'text-red-600' : 'text-amber-700'
                )}>
                  {isApproved ? 'Verified & Active' : isRejected ? 'Rejected' : 'Pending Approval'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setEditingCenter(c); resetEditForm(c); setMode('edit'); }}
                className="flex items-center gap-1 text-xs text-primary hover:underline font-medium"
              >
                <Edit3 className="w-3 h-3" />
                {isRejected ? 'Update & Re-submit' : 'Edit'}
              </button>
            </div>
            {/* Address */}
            <p className="text-sm text-foreground font-medium">
              {[c.address, c.city, c.state, c.pincode].filter(Boolean).join(', ')}
            </p>
            {/* Timeline */}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              {c.submittedAt && <span className="text-[10px] text-muted-foreground">Submitted: {new Date(c.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
              {c.approvedAt  && <span className="text-[10px] text-green-600">Approved: {new Date(c.approvedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
              {c.rejectedAt  && <span className="text-[10px] text-red-500">Rejected: {new Date(c.rejectedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
            </div>
            {isRejected && c.rejectReason && (
              <p className="text-xs text-red-500 italic">"{c.rejectReason}"</p>
            )}
          </div>
        );
      })}

      {/* Add new address button */}
      <button
        type="button"
        onClick={() => { setEditingCenter(null); resetEditForm(null); setMode('edit'); }}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-primary/40 text-primary hover:bg-primary/5 text-sm font-semibold transition-colors"
      >
        <Plus className="w-4 h-4" /> Register Another Address
      </button>

      <div className="flex justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>
    </div>
  );

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const renderEdit = () => (
    <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
      <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          Enter your pincode to auto-fill city &amp; state, add a Google Maps link, an office video tour, and up to 5 photos taken from your location for fastest admin approval.
        </p>
      </div>

      {/* ── Pincode ──────────────────────────────────────────────────────── */}
      <div>
        <Label>Pincode <span className="text-destructive">*</span></Label>
        <div className="relative mt-1">
          <Input
            placeholder="6-digit pincode"
            value={form.pincode}
            maxLength={6}
            onChange={e => handlePincodeChange(e.target.value)}
            className={cn('pr-9 font-mono tracking-widest',
              pinStatus === 'found'  ? 'border-green-400 focus:ring-green-300' :
              pinStatus === 'error'  ? 'border-amber-400' : '')}
          />
          {pinStatus === 'loading' && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
          {pinStatus === 'found'   && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
          {pinStatus === 'error'   && <AlertTriangle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />}
        </div>
        {pinStatus === 'found' && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            City &amp; state filled from pincode
            {postOffices.length > 1 && ` · ${postOffices.length} post offices in this area`}
          </p>
        )}
        {pinStatus === 'error' && (
          <p className="text-xs text-amber-600 mt-1">Pincode not found — fill city &amp; state manually below</p>
        )}
      </div>

      {/* ── City + State ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>City / District <span className="text-destructive">*</span></Label>
          <Input
            placeholder="e.g. Bangalore"
            value={form.city}
            onChange={e => set('city', e.target.value)}
            className={cn('mt-1', pinStatus === 'found' && form.city ? 'bg-green-50/60' : '')}
          />
        </div>
        <div>
          <Label>State</Label>
          <Input
            placeholder="e.g. Karnataka"
            value={form.state}
            onChange={e => set('state', e.target.value)}
            className={cn('mt-1', pinStatus === 'found' && form.state ? 'bg-green-50/60' : '')}
          />
        </div>
      </div>

      {/* ── Address ──────────────────────────────────────────────────────── */}
      <div>
        <Label>Full Address</Label>
        <Input
          placeholder="Building no., Street, Locality, Area"
          value={form.address}
          onChange={e => set('address', e.target.value)}
          className="mt-1"
        />
      </div>

      {/* ── Google Maps Link ──────────────────────────────────────────────── */}
      <div>
        <Label className="flex items-center gap-2">
          <Navigation className="w-3.5 h-3.5 text-blue-500" />
          Google Maps Location
        </Label>
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="Paste Google Maps share link or click Detect"
            value={form.mapLink}
            onChange={e => set('mapLink', e.target.value)}
            className="flex-1 text-xs"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={detectLocation}
            disabled={geoStatus === 'loading'}
            className={cn('gap-1.5 flex-shrink-0 text-xs',
              geoStatus === 'done' ? 'border-green-400 text-green-600' : 'border-blue-200 text-blue-600 hover:bg-blue-50')}
          >
            {geoStatus === 'loading'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : geoStatus === 'done'
              ? <CheckCircle2 className="w-3.5 h-3.5" />
              : <Navigation className="w-3.5 h-3.5" />}
            {geoStatus === 'loading' ? 'Detecting…' : geoStatus === 'done' ? 'Detected' : 'Detect'}
          </Button>
        </div>
        {form.coordinates?.lat && (
          <p className="text-xs text-blue-600 mt-1">
            📍 {form.coordinates.lat.toFixed(5)}, {form.coordinates.lng.toFixed(5)}
            {form.mapLink && (
              <a href={form.mapLink} target="_blank" rel="noopener noreferrer" className="ml-2 underline">
                View map
              </a>
            )}
          </p>
        )}
      </div>

      {/* ── Video Tour URL ────────────────────────────────────────────────── */}
      <div>
        <Label className="flex items-center gap-2">
          <Video className="w-3.5 h-3.5 text-purple-500" />
          Office Video Tour
          <span className="text-xs text-muted-foreground font-normal">(YouTube / Loom / Drive)</span>
        </Label>
        <div className="relative mt-1">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="https://youtube.com/..."
            value={form.videoUrl}
            onChange={e => set('videoUrl', e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* ── Office Photos ─────────────────────────────────────────────────── */}
      <div>
        <Label className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Camera className="w-3.5 h-3.5 text-primary" />
            Office Photos
            <span className="text-xs text-muted-foreground font-normal">({photos.length}/5) — taken from office location</span>
          </span>
          {photos.length < 5 && (
            <button
              type="button"
              onClick={handleAddPhoto}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium"
            >
              <ImagePlus className="w-3.5 h-3.5" /> Add Photo
            </button>
          )}
        </Label>

        {photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {photos.map((p, i) => (
              <div key={p.id || i} className="relative group aspect-square rounded-xl overflow-hidden border border-border bg-muted">
                <img src={p.preview} alt={`Office ${i + 1}`} className="w-full h-full object-cover" />
                {p.uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                )}
                {!p.uploading && p.url && (
                  <div className="absolute top-1 right-1">
                    <span className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </span>
                  </div>
                )}
                {(p.lat || p.lng) && (
                  <div className="absolute bottom-1 left-1 bg-black/60 rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                    <MapPin className="w-2.5 h-2.5 text-white" />
                    <span className="text-[9px] text-white font-medium">GPS</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(p.id)}
                  className="absolute top-1 left-1 w-5 h-5 bg-red-500 rounded-full items-center justify-center hidden group-hover:flex"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <button
                type="button"
                onClick={handleAddPhoto}
                className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
              >
                <Camera className="w-5 h-5" />
                <span className="text-[10px]">Add</span>
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAddPhoto}
            className="mt-2 w-full border-2 border-dashed border-border rounded-xl p-5 flex flex-col items-center gap-2 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
          >
            <Camera className="w-8 h-8" />
            <p className="text-sm font-medium">Take office photos</p>
            <p className="text-xs">Location will be tagged automatically</p>
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Actions */}
      <div className="flex justify-between items-center pt-3 border-t border-border">
        {offlineCenters.length > 0 ? (
          <button type="button" onClick={() => setMode('list')}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            ← Back to centers
          </button>
        ) : <span />}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            className="gradient-primary text-primary-foreground border-0 gap-2"
            onClick={handleSubmit}
            disabled={saving || photos.some(p => p.uploading)}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingCenter ? 'Update & Re-submit' : 'Submit for Approval'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            {mode === 'list'
              ? `Offline Centers (${offlineCenters.length})`
              : editingCenter ? 'Update Offline Center' : 'Register New Center'}
          </DialogTitle>
        </DialogHeader>
        {mode === 'list' ? renderList() : renderEdit()}
      </DialogContent>
    </Dialog>
  );
}

// ── Center status badge (inline in form) ─────────────────────────────────────

function CenterStatusBadge({ offlineCenter: c }) {
  if (!c?.city) return null;
  if (c.isApproved) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
        <ShieldCheck className="w-3.5 h-3.5" />
        Center verified — {c.city}{c.state ? `, ${c.state}` : ''}
      </span>
    );
  }
  if (c.rejectedAt && !c.isApproved) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
        <X className="w-3.5 h-3.5" />
        Center rejected — update required
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
      <AlertTriangle className="w-3.5 h-3.5" />
      Center pending approval — {c.city}
    </span>
  );
}

// ── Tag / Chip input ──────────────────────────────────────────────────────────
// commaTrigger=false → items added via Enter or "+" button
// commaTrigger=true  → typing a comma auto-converts text to chip

function TagInput({ items = [], onChange, placeholder = 'Type and press +', commaTrigger = false }) {
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  const add = (raw) => {
    const trimmed = raw.trim();
    if (trimmed && !items.includes(trimmed)) onChange([...items, trimmed]);
    setVal('');
  };

  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); add(val); }
    if (commaTrigger && e.key === ',') { e.preventDefault(); add(val); }
    if (e.key === 'Backspace' && !val && items.length) remove(items.length - 1);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (commaTrigger && v.endsWith(',')) { add(v.slice(0, -1)); }
    else setVal(v);
  };

  return (
    <div
      className="mt-1 rounded-xl border border-input bg-background px-3 py-2 flex flex-wrap gap-1.5 min-h-[44px] focus-within:ring-2 focus-within:ring-ring cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {items.map((item, i) => (
        <span key={i} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
          {item}
          <button type="button" onClick={() => remove(i)} className="ml-0.5 text-primary/50 hover:text-red-500 transition-colors leading-none">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <div className="flex items-center gap-1 flex-1 min-w-[120px]">
        <input
          ref={inputRef}
          value={val}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={items.length === 0 ? placeholder : ''}
          className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground min-w-0"
        />
        {!commaTrigger && (
          <button
            type="button"
            onClick={() => add(val)}
            disabled={!val.trim()}
            className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/80 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Branch / Course Multi-Select ──────────────────────────────────────────────

const LEVEL_CHIP = {
  UG:           'bg-blue-100 text-blue-700',
  PG:           'bg-purple-100 text-purple-700',
  DIPLOMA:      'bg-amber-100 text-amber-700',
  PROFESSIONAL: 'bg-green-100 text-green-700',
};

function BranchMultiSelect({ value = [], onChange }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [groups, setGroups]   = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const containerRef = useRef(null);

  // Fetch once when first opened
  useEffect(() => {
    if (!open || fetched) return;
    setLoading(true);
    Promise.all([getNsqfPublicGroups(), getNsqfPublicCourses({ limit: 500 })])
      .then(([g, c]) => {
        setGroups(g?.groups || []);
        setCourses(c?.docs  || []);
        setFetched(true);
      })
      .catch(() => toast.error('Could not load course list'))
      .finally(() => setLoading(false));
  }, [open, fetched]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (label) =>
    onChange(value.includes(label) ? value.filter(v => v !== label) : [...value, label]);

  const q = search.toLowerCase();
  const filteredGroups  = groups.filter(g =>
    !q || g.group_name.toLowerCase().includes(q) || g.group_code.toLowerCase().includes(q)
  );
  const filteredCourses = courses.filter(c =>
    !q || c.course_name.toLowerCase().includes(q) || c.course_code.toLowerCase().includes(q) ||
    (c.degree_type || '').toLowerCase().includes(q)
  );

  return (
    <div className="relative mt-1" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'w-full min-h-[42px] px-3 py-2 text-left rounded-xl border border-input bg-background',
          'flex flex-wrap gap-1.5 items-center',
          'hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring transition-colors',
          open && 'border-primary ring-2 ring-ring'
        )}
      >
        {value.length === 0 ? (
          <span className="text-sm text-muted-foreground flex-1">Select course groups or courses…</span>
        ) : (
          <>
            {value.map(v => (
              <span
                key={v}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary"
              >
                {v}
                <span
                  role="button"
                  onClick={e => { e.stopPropagation(); onChange(value.filter(x => x !== v)); }}
                  className="text-primary/50 hover:text-red-500 cursor-pointer leading-none"
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            ))}
          </>
        )}
        <ChevronDown className={cn('w-4 h-4 text-muted-foreground ml-auto shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl shadow-xl flex flex-col max-h-72 overflow-hidden">
          {/* Search box */}
          <div className="p-2 border-b border-border shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search course groups or courses…"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                {/* Course Groups section */}
                {filteredGroups.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 bg-muted/60 sticky top-0 flex items-center justify-between">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                        Course Groups · {filteredGroups.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const allGroupNames = filteredGroups.map(g => g.group_name);
                          const allSelected = allGroupNames.every(n => value.includes(n));
                          if (allSelected) onChange(value.filter(v => !allGroupNames.includes(v)));
                          else onChange([...new Set([...value, ...allGroupNames])]);
                        }}
                        className="text-[10px] font-semibold text-primary hover:underline"
                      >
                        {filteredGroups.every(g => value.includes(g.group_name)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    {filteredGroups.map(g => {
                      const selected = value.includes(g.group_name);
                      return (
                        <button
                          key={g.group_code}
                          type="button"
                          onClick={() => toggle(g.group_name)}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent transition-colors',
                            selected && 'bg-primary/5'
                          )}
                        >
                          <div className={cn(
                            'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                            selected ? 'bg-primary border-primary' : 'border-border'
                          )}>
                            {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                          </div>
                          <span className="flex-1 truncate font-medium">{g.group_name}</span>
                          <code className="text-[9px] text-muted-foreground font-mono shrink-0 hidden sm:block">{g.group_code}</code>
                          {g.level && (
                            <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0', LEVEL_CHIP[g.level] || 'bg-gray-100 text-gray-600')}>
                              {g.level}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </>
                )}

                {/* Courses section */}
                {filteredCourses.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 bg-muted/60 sticky top-0 flex items-center justify-between">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                        Individual Courses · {filteredCourses.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const allCourseNames = filteredCourses.map(c => c.course_name);
                          const allSelected = allCourseNames.every(n => value.includes(n));
                          if (allSelected) onChange(value.filter(v => !allCourseNames.includes(v)));
                          else onChange([...new Set([...value, ...allCourseNames])]);
                        }}
                        className="text-[10px] font-semibold text-primary hover:underline"
                      >
                        {filteredCourses.every(c => value.includes(c.course_name)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    {filteredCourses.map(c => {
                      const selected = value.includes(c.course_name);
                      return (
                        <button
                          key={c.course_code}
                          type="button"
                          onClick={() => toggle(c.course_name)}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent transition-colors',
                            selected && 'bg-primary/5'
                          )}
                        >
                          <div className={cn(
                            'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                            selected ? 'bg-primary border-primary' : 'border-border'
                          )}>
                            {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">{c.course_name}</p>
                            {c.degree_type && (
                              <p className="text-[11px] text-muted-foreground">{c.degree_type}</p>
                            )}
                          </div>
                          <code className="text-[9px] text-muted-foreground font-mono shrink-0 hidden sm:block">{c.course_code}</code>
                          {c.level && (
                            <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0', LEVEL_CHIP[c.level] || 'bg-gray-100 text-gray-600')}>
                              {c.level}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </>
                )}

                {filteredGroups.length === 0 && filteredCourses.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    {search ? 'No results match your search.' : 'No course data available yet.'}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer: selection count + clear */}
          {value.length > 0 && (
            <div className="px-3 py-2 border-t border-border bg-muted/40 flex items-center justify-between shrink-0">
              <span className="text-xs text-muted-foreground">{value.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Posting Form Dialog ────────────────────────────────────────────────────────

const DOMAIN_LIST = DOMAINS.filter(d => d !== 'Other');

function PostingFormDialog({ open, onClose, onSaved, editing, offlineCenters = [], onNeedRegister }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [domainIsOther, setDomainIsOther] = useState(false);
  const [locationMode, setLocationMode] = useState('center');
  const [selectedCenterId, setSelectedCenterId] = useState(null);

  // Derived: the center currently chosen in the picker
  const selectedCenter = offlineCenters.find(c => c._id === selectedCenterId) || offlineCenters[0] || null;
  const hasAnyCenters  = offlineCenters.length > 0;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const isCustom = editing.domain && !DOMAIN_LIST.includes(editing.domain);
      setDomainIsOther(isCustom);
      setForm({
        title:               editing.title || '',
        domain:              editing.domain || '',
        workType:            editing.workType || 'remote',
        location:            editing.location || '',
        description:         editing.description || '',
        responsibilities:    editing.responsibilities || [],
        requirements:        editing.requirements    || [],
        perks:               editing.perks           || [],
        skills:              editing.skills          || [],
        duration:            editing.duration || '3 months',
        startDate:           editing.startDate ? editing.startDate.slice(0, 10) : '',
        applicationDeadline: editing.applicationDeadline ? editing.applicationDeadline.slice(0, 10) : '',
        stipend:             editing.stipend || '',
        isPaid:              !!editing.isPaid,
        payType:             editing.isPaid ? (editing.payType || 'monthly') : 'free',
        payMonths:           editing.payMonths || '',
        payHours:            editing.payHours  || '',
        openings:            editing.openings || 1,
        minCgpa:             editing.eligibility?.minCgpa || 0,
        branches:            editing.eligibility?.branches || [],
        yearOfStudy:         editing.eligibility?.yearOfStudy || [],
        ppoOffered:          !!editing.ppoOffered,
        certificateOffered:  editing.certificateOffered !== false,
        status:              editing.status || 'draft',
        pocRequired:         !!editing.poc?.required,
        pocFormats:          editing.poc?.formats || [],
        pocTaskTitle:        editing.poc?.taskTitle || '',
        pocTaskDescription:  editing.poc?.taskDescription || '',
        pocSampleCode:       editing.poc?.sampleCode || '',
        pocReferenceLinks:   editing.poc?.referenceLinks || [],
        isAiRole:            !!editing.aiRole?.enabled,
        aiTools:             editing.aiRole?.tools || [],
        aiSpecializations:   editing.aiRole?.specializations || [],
        llmFrameworks:       editing.aiRole?.llmFrameworks || [],
        promptEngLevel:      editing.aiRole?.promptEngLevel || '',
      });
    } else {
      setForm(EMPTY_FORM);
      setDomainIsOther(false);
      setLocationMode('center');
      setSelectedCenterId(null);
    }
  }, [open, editing]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleYear = (y) => setForm(p => ({
    ...p,
    yearOfStudy: p.yearOfStudy.includes(y)
      ? p.yearOfStudy.filter(x => x !== y)
      : [...p.yearOfStudy, y],
  }));

  // ── Offline center gate for work type ─────────────────────────────────────
  const handleWorkTypeChange = (v) => {
    if (v === 'onsite' || v === 'hybrid') {
      if (!hasAnyCenters) { onNeedRegister(); return; }
      // Default to first center
      const first = offlineCenters[0];
      setSelectedCenterId(first._id);
      setLocationMode('center');
      const loc = [first.address, first.city, first.state].filter(Boolean).join(', ');
      setForm(p => ({ ...p, workType: v, location: loc }));
      return;
    }
    setLocationMode('center');
    setSelectedCenterId(null);
    setForm(p => ({ ...p, workType: v, location: '' }));
  };

  const selectCenter = (centerId) => {
    const c = offlineCenters.find(x => x._id === centerId);
    if (!c) return;
    setSelectedCenterId(centerId);
    const loc = [c.address, c.city, c.state].filter(Boolean).join(', ');
    set('location', loc);
  };

  const buildBody = (status) => ({
    title:       form.title.trim(),
    domain:      form.domain,
    workType:    form.workType,
    location:    form.location.trim(),
    description: form.description.trim(),
    responsibilities: form.responsibilities,
    requirements:     form.requirements,
    perks:            form.perks,
    skills:           form.skills,
    duration:    form.duration,
    startDate:   form.startDate || undefined,
    applicationDeadline: form.applicationDeadline || undefined,
    isPaid:      form.payType !== 'free',
    stipend:     form.payType !== 'free' ? Number(form.stipend) || 0 : 0,
    payType:     form.payType !== 'free' ? form.payType : null,
    payMonths:   form.payType === 'monthly' ? Number(form.payMonths) || null : null,
    payHours:    form.payType === 'weekly'  ? Number(form.payHours)  || null : null,
    openings:    Number(form.openings) || 1,
    eligibility: {
      minCgpa:     Number(form.minCgpa) || 0,
      branches:    form.branches,
      yearOfStudy: form.yearOfStudy,
    },
    ppoOffered:         form.ppoOffered,
    certificateOffered: form.certificateOffered,
    poc: form.pocRequired ? {
      required:         true,
      formats:          form.pocFormats,
      taskTitle:        form.pocTaskTitle.trim(),
      taskDescription:  form.pocTaskDescription.trim(),
      sampleCode:       form.pocSampleCode.trim(),
      referenceLinks:   form.pocReferenceLinks,
    } : null,
    aiRole: form.isAiRole ? {
      enabled:          true,
      tools:            form.aiTools,
      specializations:  form.aiSpecializations,
      llmFrameworks:    form.llmFrameworks,
      promptEngLevel:   form.promptEngLevel,
    } : null,
    status:             status || form.status,
  });

  const handleSave = async (publishNow = false) => {
    if (!form.title.trim()) { toast.error('Role title is required'); return; }
    if (!form.domain)       { toast.error('Please select a domain'); return; }
    // Extra checks only when publishing (not for drafts)
    if (publishNow && !form.description.trim()) { toast.error('Please add a description before publishing'); return; }
    if (publishNow && !form.duration)           { toast.error('Please set a duration before publishing'); return; }
    try {
      setSaving(true);
      const body = buildBody(publishNow ? 'active' : 'draft');
      if (editing) {
        await updatePosting(editing._id, body);
        toast.success(publishNow ? 'Posting published!' : 'Posting updated!');
      } else {
        await createPosting(body);
        toast.success(publishNow ? 'Internship posted and live!' : 'Saved as draft');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const needsCenter = (v) => (v === 'onsite' || v === 'hybrid') && !hasAnyCenters;
  const centerPending = offlineCenters.some(c => !c.isApproved && !c.rejectedAt);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Internship Posting' : 'Post New Internship'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Role Title <span className="text-destructive">*</span></Label>
              <Input
                placeholder="e.g. Machine Learning Intern"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Domain <span className="text-destructive">*</span></Label>
              <Select
                value={domainIsOther ? 'Other' : form.domain}
                onValueChange={v => {
                  if (v === 'Other') {
                    setDomainIsOther(true);
                    set('domain', '');
                  } else {
                    setDomainIsOther(false);
                    set('domain', v);
                  }
                }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select domain" /></SelectTrigger>
                <SelectContent>
                  {DOMAIN_LIST.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {domainIsOther && (
                <Input
                  className="mt-2"
                  placeholder="Enter domain e.g. Blockchain, Legal Tech, EdTech..."
                  value={form.domain}
                  onChange={e => set('domain', e.target.value)}
                  autoFocus
                />
              )}
            </div>

            {/* Work Type — with offline center gate */}
            <div>
              <Label>Work Type</Label>
              <Select value={form.workType} onValueChange={handleWorkTypeChange}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="onsite">
                    <span className="flex items-center gap-2">
                      Onsite
                      {!hasAnyCenters && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Lock className="w-2.5 h-2.5" /> register center
                        </span>
                      )}
                    </span>
                  </SelectItem>
                  <SelectItem value="hybrid">
                    <span className="flex items-center gap-2">
                      Hybrid
                      {!hasAnyCenters && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Lock className="w-2.5 h-2.5" /> register center
                        </span>
                      )}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {(form.workType === 'onsite' || form.workType === 'hybrid') && !hasAnyCenters && (
                <button type="button" onClick={onNeedRegister}
                  className="mt-1.5 text-xs text-primary hover:underline flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Register your offline center
                </button>
              )}
            </div>

            {/* Location — center picker for onsite/hybrid */}
            {(form.workType === 'onsite' || form.workType === 'hybrid') && (
              <div className="sm:col-span-2 space-y-2">
                <Label>Location <span className="text-destructive">*</span></Label>

                {/* One card per registered center */}
                {offlineCenters.map(c => {
                  const isSelected = selectedCenterId === c._id || (!selectedCenterId && offlineCenters[0]?._id === c._id);
                  return (
                    <button
                      key={c._id}
                      type="button"
                      onClick={() => selectCenter(c._id)}
                      className={cn(
                        'w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all',
                        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 bg-background'
                      )}
                    >
                      <div className={cn(
                        'w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                        isSelected ? 'border-primary' : 'border-muted-foreground/40'
                      )}>
                        {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground flex items-center gap-1.5 flex-wrap">
                          <Building2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          {[c.city, c.state].filter(Boolean).join(', ')}
                          {c.isApproved
                            ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Approved</span>
                            : c.rejectedAt
                              ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">Rejected</span>
                              : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                          }
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {[c.address, c.pincode].filter(Boolean).join(', ')}
                        </p>
                        {c.rejectedAt && !c.isApproved && c.rejectReason && (
                          <p className="text-[10px] text-red-500 mt-0.5 italic">"{c.rejectReason}"</p>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Location text (readonly, auto-filled from selected center) */}
                <Input value={form.location} readOnly className="bg-muted/50 cursor-default" />

                {/* Pending/rejected hint for selected center */}
                {selectedCenter && !selectedCenter.isApproved && !selectedCenter.rejectedAt && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Center approval is pending — posting can be saved but won't go live until approved.
                  </p>
                )}
                {selectedCenter?.rejectedAt && !selectedCenter.isApproved && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    This center was rejected.{' '}
                    <button type="button" onClick={() => onNeedRegister(true)} className="underline font-medium">Update & resubmit</button>
                  </p>
                )}

                {/* Register another center */}
                <button type="button" onClick={() => onNeedRegister(true)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium pt-1">
                  <Plus className="w-3.5 h-3.5" /> Register another address
                </button>
              </div>
            )}

            {/* Remote location field */}
            {form.workType === 'remote' && (
              <div className="sm:col-span-2">
                <Label>Location <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  placeholder="e.g. Anywhere in India"
                  value={form.location}
                  onChange={e => set('location', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Duration & Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Duration</Label>
              <Select value={form.duration} onValueChange={v => set('duration', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Application Deadline</Label>
              <Input type="date" value={form.applicationDeadline} onChange={e => set('applicationDeadline', e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* Compensation */}
          <div className="space-y-3">
            <Label>Compensation</Label>

            {/* Free / Monthly / Weekly selector */}
            <div className="flex gap-2">
              {[
                { value: 'free',    label: 'Unpaid / Free', color: 'border-muted-foreground/40 bg-muted/40 text-muted-foreground' },
                { value: 'monthly', label: 'Monthly',        color: 'border-green-500 bg-green-50 text-green-700' },
                { value: 'weekly',  label: 'Weekly',         color: 'border-blue-500 bg-blue-50 text-blue-700' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { set('payType', opt.value); set('isPaid', opt.value !== 'free'); }}
                  className={cn(
                    'flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all',
                    form.payType === opt.value
                      ? opt.color
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Monthly fields */}
            {form.payType === 'monthly' && (
              <div className="rounded-xl border border-green-200 bg-green-50/40 dark:bg-green-900/10 dark:border-green-800/30 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Stipend per Month (₹) <span className="text-destructive">*</span></Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        placeholder="e.g. 12000"
                        value={form.stipend}
                        onChange={e => set('stipend', e.target.value)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Duration (months)</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="e.g. 3"
                      value={form.payMonths}
                      onChange={e => set('payMonths', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                {form.stipend && (
                  <p className="text-[11px] text-green-700 dark:text-green-400 mt-2 font-medium">
                    ₹{Number(form.stipend).toLocaleString()}/month
                    {form.payMonths ? ` · Total: ₹${(Number(form.stipend) * Number(form.payMonths)).toLocaleString()} over ${form.payMonths} months` : ''}
                  </p>
                )}
              </div>
            )}

            {/* Weekly fields */}
            {form.payType === 'weekly' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 dark:bg-blue-900/10 dark:border-blue-800/30 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Stipend per Week (₹) <span className="text-destructive">*</span></Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        placeholder="e.g. 3000"
                        value={form.stipend}
                        onChange={e => set('stipend', e.target.value)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Hours per Week</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="e.g. 20"
                      value={form.payHours}
                      onChange={e => set('payHours', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                {form.stipend && (
                  <p className="text-[11px] text-blue-700 dark:text-blue-400 mt-2 font-medium">
                    ₹{Number(form.stipend).toLocaleString()}/week
                    {form.payHours ? ` · ${form.payHours} hrs/week` : ''}
                    {form.stipend ? ` · ~₹${(Number(form.stipend) * 4).toLocaleString()}/mo est.` : ''}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Openings */}
          <div>
            <Label>Openings</Label>
            <Input type="number" min="1" value={form.openings} onChange={e => set('openings', e.target.value)} className="mt-1 w-24" />
          </div>

          {/* Description */}
          <div>
            <Label>About the Role</Label>
            <Textarea
              placeholder="Describe what the intern will be doing, the team, and the opportunity..."
              value={form.description}
              onChange={e => set('description', e.target.value)}
              className="mt-1 min-h-[90px]"
            />
          </div>

          {/* Responsibilities & Requirements */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1.5">Responsibilities</Label>
              <TagInput
                items={form.responsibilities}
                onChange={v => set('responsibilities', v)}
                placeholder="e.g. Build and maintain features…"
              />
            </div>
            <div>
              <Label>Requirements</Label>
              <TagInput
                items={form.requirements}
                onChange={v => set('requirements', v)}
                placeholder="e.g. Knowledge of Python…"
              />
            </div>
          </div>

          {/* Perks */}
          <div>
            <Label>Perks & Benefits</Label>
            <TagInput
              items={form.perks}
              onChange={v => set('perks', v)}
              placeholder="e.g. Letter of Recommendation…"
            />
          </div>

          {/* Skills */}
          <div>
            <Label className="flex items-center gap-1.5">
              Required Skills
              <span className="text-[10px] text-muted-foreground font-normal">type skill, press comma or Enter</span>
            </Label>
            <TagInput
              items={form.skills}
              onChange={v => set('skills', v)}
              placeholder="e.g. Python, TensorFlow…"
              commaTrigger
            />
          </div>

          {/* Eligibility */}
          <div className="p-4 rounded-xl bg-muted/40 space-y-3">
            <p className="font-medium text-sm">Eligibility Criteria</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Min CGPA</Label>
                <Input type="number" step="0.1" min="0" max="10" placeholder="e.g. 6.5" value={form.minCgpa || ''} onChange={e => set('minCgpa', e.target.value)} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label>Eligible Branches / Courses</Label>
                <BranchMultiSelect value={form.branches} onChange={v => set('branches', v)} />
              </div>
            </div>
            <div>
              <Label className="block mb-2">Year of Study</Label>
              <div className="flex flex-wrap gap-2">
                {['1st Year', '2nd Year', '3rd Year', '4th Year', 'Final Year', 'Passout'].map(y => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => toggleYear(y)}
                    className={cn(
                      'text-xs px-3 py-1.5 rounded-full border transition-colors',
                      form.yearOfStudy.includes(y)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-muted-foreground hover:border-primary/50'
                    )}
                  >{y}</button>
                ))}
              </div>
            </div>
          </div>

          {/* ── AI / Prompt Engineer Section ───────────────────────────── */}
          <div className="rounded-xl border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => set('isAiRole', !form.isAiRole)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                form.isAiRole ? 'bg-violet-50 border-b border-violet-200' : 'bg-muted/30 hover:bg-muted/60'
              )}
            >
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', form.isAiRole ? 'bg-violet-600' : 'bg-muted')}>
                <Bot className={cn('w-4 h-4', form.isAiRole ? 'text-white' : 'text-muted-foreground')} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">AI / Prompt Engineer Role</p>
                <p className="text-xs text-muted-foreground">Enable for roles requiring AI tools, prompt engineering or LLM expertise</p>
              </div>
              <div className={cn('w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                form.isAiRole ? 'bg-violet-600 border-violet-600' : 'border-border bg-background')}>
                {form.isAiRole && <Check className="w-3 h-3 text-white" />}
              </div>
            </button>

            {form.isAiRole && (
              <div className="p-4 space-y-5 bg-violet-50/40">
                {/* AI Tools multi-select */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                    Required AI Tools
                    <span className="text-[10px] text-muted-foreground font-normal">select all that apply</span>
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AI_TOOLS.map(t => {
                      const sel = form.aiTools.includes(t);
                      return (
                        <button key={t} type="button"
                          onClick={() => set('aiTools', sel ? form.aiTools.filter(x => x !== t) : [...form.aiTools, t])}
                          className={cn('text-xs px-3 py-1.5 rounded-full border transition-all',
                            sel ? 'bg-violet-600 text-white border-violet-600' : 'bg-background border-border text-muted-foreground hover:border-violet-400'
                          )}>
                          {sel && <Check className="w-3 h-3 inline mr-1" />}{t}
                        </button>
                      );
                    })}
                    <button type="button"
                      onClick={() => set('aiTools', form.aiTools.length === AI_TOOLS.length ? [] : [...AI_TOOLS])}
                      className="text-xs px-3 py-1.5 rounded-full border border-dashed border-violet-400 text-violet-600 hover:bg-violet-50 transition-all font-semibold">
                      {form.aiTools.length === AI_TOOLS.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                </div>

                {/* AI Specializations */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1">
                    <FlaskConical className="w-3.5 h-3.5 text-violet-600" />
                    AI Specializations
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AI_SPECIALIZATIONS.map(s => {
                      const sel = form.aiSpecializations.includes(s);
                      return (
                        <button key={s} type="button"
                          onClick={() => set('aiSpecializations', sel ? form.aiSpecializations.filter(x => x !== s) : [...form.aiSpecializations, s])}
                          className={cn('text-xs px-3 py-1.5 rounded-full border transition-all',
                            sel ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background border-border text-muted-foreground hover:border-indigo-400'
                          )}>
                          {sel && <Check className="w-3 h-3 inline mr-1" />}{s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* LLM Frameworks */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1">
                    <Code2 className="w-3.5 h-3.5 text-violet-600" />
                    LLM Frameworks / SDKs
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {LLM_FRAMEWORKS.map(f => {
                      const sel = form.llmFrameworks.includes(f);
                      return (
                        <button key={f} type="button"
                          onClick={() => set('llmFrameworks', sel ? form.llmFrameworks.filter(x => x !== f) : [...form.llmFrameworks, f])}
                          className={cn('text-xs px-3 py-1.5 rounded-full border font-mono transition-all',
                            sel ? 'bg-sky-600 text-white border-sky-600' : 'bg-background border-border text-muted-foreground hover:border-sky-400'
                          )}>
                          {sel && <Check className="w-3 h-3 inline mr-1" />}{f}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Prompt Engineering Level */}
                <div>
                  <Label className="mb-2 block">Prompt Engineering Level Required</Label>
                  <div className="flex gap-2 flex-wrap">
                    {PROMPT_ENG_LEVELS.map(l => (
                      <button key={l} type="button"
                        onClick={() => set('promptEngLevel', form.promptEngLevel === l ? '' : l)}
                        className={cn('text-xs px-4 py-2 rounded-xl border-2 font-semibold transition-all',
                          form.promptEngLevel === l
                            ? 'bg-violet-600 text-white border-violet-600'
                            : 'border-border text-muted-foreground hover:border-violet-400'
                        )}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── POC / Assignment Section ────────────────────────────────────── */}
          <div className="rounded-xl border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => set('pocRequired', !form.pocRequired)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                form.pocRequired ? 'bg-amber-50 border-b border-amber-200' : 'bg-muted/30 hover:bg-muted/60'
              )}
            >
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', form.pocRequired ? 'bg-amber-500' : 'bg-muted')}>
                <BookOpen className={cn('w-4 h-4', form.pocRequired ? 'text-white' : 'text-muted-foreground')} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Require POC / Assignment Submission</p>
                <p className="text-xs text-muted-foreground">Ask applicants to submit a proof of concept, task or work sample</p>
              </div>
              <div className={cn('w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                form.pocRequired ? 'bg-amber-500 border-amber-500' : 'border-border bg-background')}>
                {form.pocRequired && <Check className="w-3 h-3 text-white" />}
              </div>
            </button>

            {form.pocRequired && (
              <div className="p-4 space-y-5 bg-amber-50/30">
                {/* Task title */}
                <div>
                  <Label>Task / Assignment Title <span className="text-destructive">*</span></Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g. Build a RAG pipeline for a given dataset"
                    value={form.pocTaskTitle}
                    onChange={e => set('pocTaskTitle', e.target.value)}
                  />
                </div>

                {/* Detailed task description */}
                <div>
                  <Label>Detailed Task Description</Label>
                  <Textarea
                    className="mt-1 min-h-[100px] font-mono text-sm"
                    placeholder="Describe the task in detail — what to build, expected output, evaluation criteria, edge cases to handle…"
                    value={form.pocTaskDescription}
                    onChange={e => set('pocTaskDescription', e.target.value)}
                  />
                </div>

                {/* Accepted submission formats */}
                <div>
                  <Label className="mb-2 block">Accepted Submission Formats</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {POC_FORMATS.map(f => {
                      const sel = form.pocFormats.includes(f.id);
                      const Icon = f.icon;
                      return (
                        <button key={f.id} type="button"
                          onClick={() => set('pocFormats', sel ? form.pocFormats.filter(x => x !== f.id) : [...form.pocFormats, f.id])}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all text-left',
                            sel ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-border text-muted-foreground hover:border-amber-400'
                          )}>
                          <Icon className={cn('w-4 h-4 shrink-0', sel ? 'text-amber-600' : 'text-muted-foreground')} />
                          {f.label}
                          {sel && <Check className="w-3.5 h-3.5 ml-auto text-amber-600" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sample code */}
                <div>
                  <Label className="flex items-center gap-1.5">
                    <Code2 className="w-3.5 h-3.5 text-amber-600" />
                    Sample Code / Starter Template
                    <span className="text-[10px] text-muted-foreground font-normal ml-1">optional</span>
                  </Label>
                  <Textarea
                    className="mt-1 min-h-[80px] font-mono text-xs"
                    placeholder={`# Paste starter code, schema, or any template here\ndef main():\n    pass`}
                    value={form.pocSampleCode}
                    onChange={e => set('pocSampleCode', e.target.value)}
                  />
                </div>

                {/* Reference links */}
                <div>
                  <Label className="flex items-center gap-1.5 mb-1">
                    <Link className="w-3.5 h-3.5 text-amber-600" />
                    Reference Links
                    <span className="text-[10px] text-muted-foreground font-normal ml-1">docs, papers, repos, datasets</span>
                  </Label>
                  <TagInput
                    items={form.pocReferenceLinks}
                    onChange={v => set('pocReferenceLinks', v)}
                    placeholder="Paste a URL and press +"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Feature flags */}
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.ppoOffered} onCheckedChange={v => set('ppoOffered', v)} id="ppo" />
              <Label htmlFor="ppo" className="cursor-pointer">PPO Offered</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.certificateOffered} onCheckedChange={v => set('certificateOffered', v)} id="cert" />
              <Label htmlFor="cert" className="cursor-pointer">Certificate Provided</Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Save as Draft
            </Button>
            <Button className="gradient-primary text-primary-foreground border-0" onClick={() => handleSave(true)} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              {editing ? 'Update & Publish' : 'Publish Now'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Kanban card ────────────────────────────────────────────────────────────────

function KanbanCard({ app, currentStage, onMove, onViewDetail }) {
  const nextStageMap = { applied: 'screening', screening: 'interview', interview: 'hired' };
  const nextStage = nextStageMap[currentStage];

  return (
    <Card className="border-border shadow-sm hover:shadow-card transition-shadow cursor-pointer group" onClick={() => onViewDetail?.(app._id)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-foreground truncate flex-1 mr-2 group-hover:text-primary transition-colors">
            {app.studentName || 'Applicant'}
          </span>
          {(app.poc?.submittedAt || app.aiEvaluation?.evaluatedAt) && (
            <div className="flex gap-1 shrink-0">
              {app.poc?.submittedAt    && <span title="POC submitted" className="text-[9px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded-full font-bold">POC</span>}
              {app.aiEvaluation?.evaluatedAt && <span title="AI evaluated" className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><Sparkles className="w-2 h-2" />{app.aiEvaluation.overallScore}</span>}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-1 truncate">
          {app.posting_id?.title || '—'}
        </p>
        {app.studentCollege && (
          <p className="text-xs text-muted-foreground mb-2 truncate">{app.studentCollege}</p>
        )}
        <p className="text-xs text-muted-foreground mb-2">
          {new Date(app.createdAt || app.stageUpdatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </p>
        {nextStage && (
          <button onClick={() => onMove(app._id, nextStage)} className="text-xs text-primary hover:text-primary/80 font-medium">
            Move → {STAGE_LABELS[nextStage]}
          </button>
        )}
        {currentStage === 'interview' && (
          <div className="flex gap-2 mt-1">
            <button onClick={() => onMove(app._id, 'hired')} className="text-xs text-green-600 font-medium">✓ Hire</button>
            <button onClick={() => onMove(app._id, 'rejected')} className="text-xs text-red-500 font-medium">✗ Reject</button>
          </div>
        )}
        {currentStage === 'applied' && (
          <button onClick={() => onMove(app._id, 'rejected')} className="text-xs text-red-500 font-medium mt-1">✗ Reject</button>
        )}
        {currentStage === 'screening' && (
          <button onClick={() => onMove(app._id, 'rejected')} className="text-xs text-red-500 font-medium mt-1">✗ Reject</button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Stage Transition Dialog ───────────────────────────────────────────────────

const STAGE_CONFIG = {
  screening: {
    label: 'Move to Screening',
    icon: Search,
    color: 'text-amber-500',
    bg: 'bg-amber-50 border-amber-200',
    desc: 'Notify the applicant their application is being reviewed.',
  },
  interview: {
    label: 'Schedule Interview',
    icon: CalendarDays,
    color: 'text-violet-600',
    bg: 'bg-violet-50 border-violet-200',
    desc: 'Set the interview date, meeting link and send calendar details to the candidate.',
  },
  hired: {
    label: 'Select Candidate',
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
    desc: 'Confirm the offer details and notify the student of their selection.',
  },
  rejected: {
    label: 'Reject Application',
    icon: X,
    color: 'text-red-500',
    bg: 'bg-red-50 border-red-200',
    desc: 'Send a respectful rejection with optional feedback.',
  },
};

const REJECTION_REASONS = [
  'Not the right fit for this role',
  'Position has been filled',
  'Insufficient experience or skills',
  'Better matching candidates selected',
  'Budget constraints',
  'Application was incomplete',
];

function StageTransitionDialog({ open, targetStage, app, posting, onClose, onConfirm }) {
  const [saving, setSaving]   = useState(false);
  const [note, setNote]       = useState('');
  // interview
  const [interviewAt, setInterviewAt]       = useState('');
  const [interviewLink, setInterviewLink]   = useState('');
  const [interviewLocation, setInterviewLocation] = useState('');
  // offer
  const [offerStartDate, setOfferStartDate] = useState('');
  const [confirmedStipend, setConfirmedStipend] = useState(posting?.stipend || '');
  const [offerNote, setOfferNote]           = useState('');
  // rejection
  const [rejectionReason, setRejectionReason]   = useState(REJECTION_REASONS[0]);
  const [rejectionMessage, setRejectionMessage] = useState('');

  useEffect(() => {
    if (open) {
      setNote(''); setInterviewAt(''); setInterviewLink(''); setInterviewLocation('');
      setOfferStartDate(''); setConfirmedStipend(posting?.stipend || '');
      setOfferNote(''); setRejectionReason(REJECTION_REASONS[0]); setRejectionMessage('');
    }
  }, [open, posting]);

  const cfg = STAGE_CONFIG[targetStage] || {};
  const Icon = cfg.icon || Briefcase;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const body = { stage: targetStage, note };
      if (targetStage === 'interview') {
        if (!interviewAt) { toast.error('Please set an interview date and time'); setSaving(false); return; }
        Object.assign(body, { interviewAt, interviewLink, interviewLocation });
      }
      if (targetStage === 'hired') {
        Object.assign(body, { offerStartDate, confirmedStipend: Number(confirmedStipend) || null, offerNote });
      }
      if (targetStage === 'rejected') {
        Object.assign(body, { rejectionReason, rejectionMessage });
      }
      await onConfirm(body);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally { setSaving(false); }
  };

  if (!open || !targetStage) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', cfg.bg?.split(' ')[0])}>
              <Icon className={cn('w-4 h-4', cfg.color)} />
            </div>
            {cfg.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <p className="text-sm text-muted-foreground">{cfg.desc}</p>

          {/* Applicant summary */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
            <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
              {(app?.studentName?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{app?.studentName}</p>
              <p className="text-xs text-muted-foreground">{app?.studentCollege} · {app?.studentEmail}</p>
            </div>
          </div>

          {/* ── Screening ── */}
          {targetStage === 'screening' && (
            <div>
              <Label className="text-xs">Message to applicant <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea className="mt-1.5 min-h-[80px] text-sm resize-none"
                placeholder="We have received your application and are currently reviewing it..."
                value={note} onChange={e => setNote(e.target.value)} />
            </div>
          )}

          {/* ── Interview ── */}
          {targetStage === 'interview' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Date & Time <span className="text-destructive">*</span></Label>
                <Input type="datetime-local" className="mt-1 text-sm" value={interviewAt} onChange={e => setInterviewAt(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Meeting Link</Label>
                <Input className="mt-1 text-sm" placeholder="https://meet.google.com/… or Teams/Zoom link" value={interviewLink} onChange={e => setInterviewLink(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Location <span className="text-muted-foreground font-normal">(for in-person)</span></Label>
                <Input className="mt-1 text-sm" placeholder="e.g. Office address or Online" value={interviewLocation} onChange={e => setInterviewLocation(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Message to candidate <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea className="mt-1.5 min-h-[70px] text-sm resize-none"
                  placeholder="Please come prepared with your portfolio and any relevant work..."
                  value={note} onChange={e => setNote(e.target.value)} />
              </div>
            </div>
          )}

          {/* ── Hired ── */}
          {targetStage === 'hired' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" className="mt-1 text-sm" value={offerStartDate} onChange={e => setOfferStartDate(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Confirmed Stipend (₹/mo)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
                    <Input type="number" className="pl-6 text-sm" placeholder="e.g. 15000" value={confirmedStipend} onChange={e => setConfirmedStipend(e.target.value)} />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Offer message</Label>
                <Textarea className="mt-1.5 min-h-[80px] text-sm resize-none"
                  placeholder="We're excited to welcome you to the team! Please confirm your acceptance by..."
                  value={offerNote} onChange={e => setOfferNote(e.target.value)} />
              </div>
            </div>
          )}

          {/* ── Rejected ── */}
          {targetStage === 'rejected' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Reason</Label>
                <select className="mt-1 w-full text-sm px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}>
                  {REJECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Personal message <span className="text-muted-foreground font-normal">(optional — shown to candidate)</span></Label>
                <Textarea className="mt-1.5 min-h-[80px] text-sm resize-none"
                  placeholder="Thank you for your interest. We appreciated reviewing your profile and encourage you to apply again in the future..."
                  value={rejectionMessage} onChange={e => setRejectionMessage(e.target.value)} />
              </div>
            </div>
          )}

          <div className={cn('flex items-start gap-2 p-2.5 rounded-lg text-xs',
            targetStage === 'rejected' ? 'bg-muted/40' : 'bg-blue-50 border border-blue-100')}>
            <span className={targetStage === 'rejected' ? 'text-muted-foreground' : 'text-blue-600'}>
              {targetStage === 'rejected'
                ? '💌 A respectful notification email will be sent to the applicant.'
                : `📧 An email notification will be sent to ${app?.studentEmail}.`}
            </span>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving} className="flex-1">Cancel</Button>
            <Button onClick={handleConfirm} disabled={saving}
              className={cn('flex-1 font-semibold gap-1.5',
                targetStage === 'rejected' ? 'bg-red-500 hover:bg-red-600 text-white border-0' :
                targetStage === 'hired'    ? 'gradient-primary text-primary-foreground border-0' :
                'gradient-primary text-primary-foreground border-0')}>
              {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Sending…</> : <><Icon className="w-4 h-4" />{cfg.label}</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Application Detail Panel (startup side) ───────────────────────────────────

const REC_STYLE = {
  strongly_recommend: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  recommend:          'bg-blue-500/10 text-blue-500 border-blue-500/20',
  neutral:            'bg-amber-500/10 text-amber-500 border-amber-500/20',
  not_recommend:      'bg-red-500/10 text-red-500 border-red-500/20',
};

function ApplicationDetailPanel({ appId, onClose, onStageChange, openStage }) {
  const [detail, setDetail]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [evaluating, setEvaluating]   = useState(false);
  const [transitionStage, setTransitionStage] = useState(null); // which stage dialog is open
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // AI-eval consent (STN_AI_EVAL_GATE): null = unknown/not checked yet,
  // 'blocked' = 403'd, no request sent, 'pending' = request sent, awaiting
  // the applicant's response. Cleared back to null once an evaluation
  // actually succeeds.
  const [consentState, setConsentState] = useState(null);
  const [requestingConsent, setRequestingConsent] = useState(false);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    setConsentState(null);
    getApplicationDetail(appId)
      .then(d => {
        setDetail(d?.data ?? d);   // unwrap { success, data: {...} } envelope
        // Opened from a Kanban "Move → Interview" / "Hire" button: go straight to that stage's dialog, which asks
        // for the interview date or the offer details the email to the student needs.
        if (openStage) setTransitionStage(openStage);
      })
      .catch(() => toast.error('Could not load application'))
      .finally(() => setLoading(false));
    // Pre-check consent status so a founder re-opening this panel sees
    // "request sent" / already-granted state without clicking Evaluate first.
    getAiEvalConsentStatus(appId)
      .then(res => {
        const data = res?.data ?? res;
        if (data?.granted) setConsentState(null);
        else if (data?.pendingRequestId) setConsentState('pending');
      })
      .catch(() => {});
  }, [appId]);

  // Resumes come as a 15-minute link asked for at click time (stored links are never handed out). The tab is
  // opened first so the browser does not treat it as an unrequested pop-up.
  const openResume = async () => {
    const w = window.open('', '_blank');
    try {
      const r = await getApplicantResume(appId);
      const url = r?.data?.url || r?.url;
      if (!url) throw new Error('No resume link');
      if (w) w.location.href = url; else window.location.href = url;
    } catch (err) {
      if (w) w.close();
      toast.error(err?.message || 'Could not open the resume');
    }
  };

  const handleEval = async () => {
    setEvaluating(true);
    try {
      const res = await triggerAiEvaluation(appId);
      const ev  = res?.data ?? res;               // unwrap { success, data: evaluation }
      setDetail(p => ({ ...p, aiEvaluation: ev }));
      setConsentState(null);
      toast.success('AI evaluation complete!');
    } catch (err) {
      if (err?.errCode === 'CONSENT_REQUIRED') {
        setConsentState(prev => (prev === 'pending' ? 'pending' : 'blocked'));
        toast.error(err.message || 'Applicant consent required for AI review.');
      } else {
        toast.error('Evaluation failed');
      }
    }
    finally { setEvaluating(false); }
  };

  const handleRequestConsent = async () => {
    setRequestingConsent(true);
    try {
      const res = await requestAiEvalConsent(appId);
      const data = res?.data ?? res;
      if (data?.alreadyGranted) {
        setConsentState(null);
        toast.success('Consent already granted — you can evaluate now.');
      } else {
        setConsentState('pending');
        toast.success(data?.resent ? 'Reminder sent to the applicant.' : 'Consent request sent to the applicant.');
      }
    } catch (err) {
      toast.error(err?.message || 'Could not send consent request');
    }
    finally { setRequestingConsent(false); }
  };

  const handleStageConfirm = async (body) => {
    await updateApplicationStage(appId, body);
    setDetail(p => ({ ...p, stage: body.stage, stageUpdatedAt: new Date().toISOString(),
      ...(body.stage === 'interview' ? { interviewDetails: { scheduledAt: body.interviewAt, link: body.interviewLink, location: body.interviewLocation, note: body.note } } : {}),
      ...(body.stage === 'hired'     ? { offerDetails: { startDate: body.offerStartDate, confirmedStipend: body.confirmedStipend, note: body.offerNote } } : {}),
      ...(body.stage === 'rejected'  ? { rejectionDetails: { reason: body.rejectionReason, message: body.rejectionMessage } } : {}),
    }));
    onStageChange?.(appId, body.stage);
    toast.success(`✓ ${STAGE_LABELS[body.stage]} — email sent to ${detail?.studentName}`);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-[560px] bg-background border-l border-border z-50 flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
          <h2 className="font-bold text-foreground">Application Detail</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : !detail ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Could not load application.</div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Applicant header */}
            <div className="p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                  {(detail.studentName?.[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground">{detail.studentName}</p>
                  {/* Withdrawn, or closed 90+ days ago: the student no longer shares their details with you. */}
                  {detail.detailsAccessNote && <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">{detail.detailsAccessNote}</p>}
                  <p className="text-sm text-muted-foreground">{detail.studentCollege}</p>
                  <p className="text-xs text-muted-foreground">{detail.studentEmail}</p>
                </div>
                <div className={cn('text-xs font-bold px-3 py-1.5 rounded-full border capitalize shrink-0', STAGE_COLORS[detail.stage])}>
                  {STAGE_LABELS[detail.stage]}
                </div>
              </div>

              {/* Quick info */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {detail.studentCgpa > 0 && (
                  <div className="text-center p-2 rounded-lg bg-muted/40 border border-border">
                    <p className="text-lg font-black text-foreground">{detail.studentCgpa}</p>
                    <p className="text-[10px] text-muted-foreground">CGPA</p>
                  </div>
                )}
                {(detail.resumeAvailable || detail.resumeUrl) && (
                  <button type="button" onClick={openResume}
                    className="flex flex-col items-center p-2 rounded-lg bg-muted/40 border border-border hover:border-primary/40 transition-colors">
                    <FileText className="w-4 h-4 text-primary mb-1" />
                    <p className="text-[10px] text-muted-foreground">Resume</p>
                  </button>
                )}
                {detail.linkedinUrl && (
                  <a href={detail.linkedinUrl} target="_blank" rel="noopener noreferrer"
                    className="flex flex-col items-center p-2 rounded-lg bg-muted/40 border border-border hover:border-primary/40 transition-colors">
                    <ExternalLink className="w-4 h-4 text-primary mb-1" />
                    <p className="text-[10px] text-muted-foreground">LinkedIn</p>
                  </a>
                )}
              </div>
            </div>

            {/* Cover letter */}
            {detail.coverLetter && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Cover Letter</p>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{detail.coverLetter}</p>
              </div>
            )}

            {/* POC submission */}
            {detail.poc && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">POC Submission</p>
                <div className={cn('flex items-start gap-3 p-3 rounded-xl border text-sm mb-3',
                  detail.poc.status === 'passed' ? 'bg-emerald-500/5 border-emerald-500/20' :
                  detail.poc.status === 'failed' ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20')}>
                  <BookOpen className={cn('w-4 h-4 shrink-0 mt-0.5',
                    detail.poc.status === 'passed' ? 'text-emerald-500' : detail.poc.status === 'failed' ? 'text-red-500' : 'text-amber-500')} />
                  <div className="flex-1 min-w-0">
                    {detail.poc.type === 'github' ? (
                      <div>
                        <p className="font-semibold text-foreground flex items-center gap-1.5">
                          <Github className="w-3.5 h-3.5" />
                          {detail.poc.githubOwner}/{detail.poc.githubRepo}
                          <span className="text-muted-foreground font-normal text-xs">({detail.poc.githubBranch})</span>
                        </p>
                        <a href={`https://github.com/${detail.poc.githubOwner}/${detail.poc.githubRepo}/tree/${detail.poc.githubBranch}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
                          <ExternalLink className="w-3 h-3" /> View on GitHub
                        </a>
                      </div>
                    ) : (
                      <div>
                        <p className="font-semibold text-foreground">{detail.poc.fileName || 'Submission'}</p>
                        {detail.poc.fileUrl && (
                          <a href={detail.poc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
                            <ExternalLink className="w-3 h-3" /> Open file
                          </a>
                        )}
                      </div>
                    )}
                    <span className="text-[10px] capitalize text-muted-foreground mt-1 block">Status: {detail.poc.status}</span>
                  </div>
                </div>

                {/* GitHub commits */}
                {detail.poc.type === 'github' && detail.commits?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                      <GitCommit className="w-3.5 h-3.5 text-primary" /> {detail.commits.length} Commits on {detail.poc.githubBranch}
                    </p>
                    <div className="rounded-xl border border-border overflow-hidden max-h-52 overflow-y-auto bg-muted/20">
                      {detail.commits.map((c, i) => (
                        <div key={c.sha} className={cn('flex items-start gap-3 px-3 py-2.5 hover:bg-accent/40 transition-colors', i > 0 && 'border-t border-border/50')}>
                          <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded mt-0.5 shrink-0">{c.sha}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">{c.message}</p>
                            <p className="text-[10px] text-muted-foreground">{c.author} · {new Date(c.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                          </div>
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary shrink-0"><ExternalLink className="w-3 h-3" /></a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Evaluation (not offered once the applicant's details are no longer shared; the server refuses it too) */}
            <div className={`p-5 border-b border-border ${detail.detailsAccess && detail.detailsAccess !== 'shared' ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" /> AI Evaluation
                </p>
                <Button size="sm" onClick={handleEval} disabled={evaluating}
                  className="h-7 text-xs gradient-primary text-primary-foreground border-0 gap-1.5">
                  {evaluating ? <><RefreshCw className="w-3 h-3 animate-spin" /> Evaluating…</> : <><Sparkles className="w-3 h-3" />{detail.aiEvaluation?.evaluatedAt ? 'Re-evaluate' : 'Evaluate with AI'}</>}
                </Button>
              </div>

              {detail.aiEvaluation?.evaluatedAt ? (
                <div className="space-y-4">
                  {/* Score */}
                  <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-muted/20">
                    <div className="text-center">
                      <p className="text-4xl font-black text-foreground">{detail.aiEvaluation.overallScore}</p>
                      <p className="text-[10px] text-muted-foreground">/100</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Overall Score</span>
                        <span className="font-bold text-foreground">{detail.aiEvaluation.overallScore}%</span>
                      </div>
                      <Progress value={detail.aiEvaluation.overallScore} className="h-2" />
                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{detail.aiEvaluation.developerLevel}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border">Skill match: {detail.aiEvaluation.skillMatch}%</span>
                        {detail.aiEvaluation.recommendation && (
                          <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize border',
                            REC_STYLE[detail.aiEvaluation.recommendation] || 'bg-muted/60 text-muted-foreground border-border')}>
                            {detail.aiEvaluation.recommendation.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <p className="text-sm text-muted-foreground leading-relaxed">{detail.aiEvaluation.summary}</p>

                  {/* Strengths + Improvements */}
                  <div className="grid grid-cols-2 gap-3">
                    {detail.aiEvaluation.strengths?.length > 0 && (
                      <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">Strengths</p>
                        <ul className="space-y-1">
                          {detail.aiEvaluation.strengths.map((s, i) => <li key={i} className="text-xs text-foreground flex items-start gap-1"><span className="text-emerald-500 shrink-0">›</span>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {detail.aiEvaluation.improvements?.length > 0 && (
                      <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">Improvements</p>
                        <ul className="space-y-1">
                          {detail.aiEvaluation.improvements.map((s, i) => <li key={i} className="text-xs text-foreground flex items-start gap-1"><span className="text-amber-500 shrink-0">›</span>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Commit analysis */}
                  {detail.aiEvaluation.commitAnalysis && (
                    <div className="p-3 rounded-xl bg-muted/30 border border-border">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Commit Analysis</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">Commits: <strong className="text-foreground">{detail.aiEvaluation.commitAnalysis.totalCommits}</strong></span>
                        <span className="text-muted-foreground">Quality: <strong className="text-foreground">{detail.aiEvaluation.commitAnalysis.commitQuality}</strong></span>
                      </div>
                      {detail.aiEvaluation.commitAnalysis.codePatterns?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {detail.aiEvaluation.commitAnalysis.codePatterns.map((p, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/5 text-primary border border-primary/20">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-[10px] text-muted-foreground">Evaluated {new Date(detail.aiEvaluation.evaluatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              ) : consentState === 'blocked' || consentState === 'pending' ? (
                <div className="text-center py-8 px-4 text-sm border border-dashed border-amber-500/30 bg-amber-500/5 rounded-xl">
                  <Lock className="w-8 h-8 text-amber-500/60 mx-auto mb-2" />
                  <p className="text-foreground font-medium mb-1">Applicant consent required</p>
                  <p className="text-muted-foreground text-xs max-w-sm mx-auto mb-4">
                    This applicant hasn't agreed to AI review of their project and GitHub activity yet.
                    {consentState === 'pending'
                      ? ' A request has been sent — you can evaluate as soon as they accept.'
                      : ' Send them a request; AI evaluation unlocks automatically once they accept.'}
                  </p>
                  <Button size="sm" variant="outline" onClick={handleRequestConsent}
                    disabled={requestingConsent || consentState === 'pending'}
                    className="h-7 text-xs gap-1.5">
                    {requestingConsent
                      ? <><RefreshCw className="w-3 h-3 animate-spin" /> Sending…</>
                      : consentState === 'pending'
                        ? <><ShieldCheck className="w-3 h-3" /> Request sent</>
                        : <><ShieldCheck className="w-3 h-3" /> Request consent</>}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
                  <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  Click "Evaluate with AI" to analyze this applicant's profile, cover letter, and commit history.
                </div>
              )}
            </div>

            {/* Verified Hiring Reports — student-selected, mentor-published, evidence-checked
                against real repo content and commit history. Independent of the AI
                Evaluation card above (that's an on-demand company-triggered evaluation;
                these are auto-generated on platform project completion). */}
            {detail.hiringReports?.length > 0 && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-3">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Verified Hiring Reports
                </p>
                <div className="space-y-3">
                  {detail.hiringReports.map((r, i) => (
                    <div key={i} className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-sm font-bold text-foreground">{r.projectTitle}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 font-semibold flex items-center gap-1 shrink-0">
                          <ShieldCheck className="w-2.5 h-2.5" /> {r.overallDeveloperScore}/100
                        </span>
                      </div>
                      {r.featuresNarrative && (
                        <p className="text-xs text-muted-foreground leading-relaxed mb-2">{r.featuresNarrative}</p>
                      )}
                      {r.techStackVerified?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {r.techStackVerified.filter((t) => t.status === 'verified').map((t, j) => (
                            <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/5 text-primary border border-primary/20 flex items-center gap-1">
                              <Code2 className="w-2.5 h-2.5" /> {t.tech}
                            </span>
                          ))}
                        </div>
                      )}
                      {r.recommendationSummary && (
                        <p className="text-xs text-foreground italic mb-2">"{r.recommendationSummary}"</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-muted-foreground">
                          {r.verificationCode} · {r.generatedAt ? new Date(r.generatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </span>
                        {r.pdfUrl && (
                          <a href={r.pdfUrl} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] px-2 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-semibold flex items-center gap-1">
                            <ExternalLink className="w-2.5 h-2.5" /> View report
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Stage-specific detail blocks ── */}
            {detail.stage === 'interview' && detail.interviewDetails && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-violet-500" /> Interview Details</p>
                <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 space-y-2">
                  {detail.interviewDetails.scheduledAt && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">When</span>
                      <span className="font-semibold text-foreground">{new Date(detail.interviewDetails.scheduledAt).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</span>
                    </div>
                  )}
                  {detail.interviewDetails.link && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">Link</span>
                      <a href={detail.interviewDetails.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate text-xs">{detail.interviewDetails.link}</a>
                    </div>
                  )}
                  {detail.interviewDetails.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">Location</span>
                      <span className="text-foreground text-xs">{detail.interviewDetails.location}</span>
                    </div>
                  )}
                  {detail.interviewDetails.note && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">Note sent</span>
                      <span className="text-muted-foreground text-xs italic">"{detail.interviewDetails.note}"</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {detail.stage === 'hired' && detail.offerDetails && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Offer Details</p>
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-2">
                  {detail.offerDetails.startDate && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-28 shrink-0 text-xs">Start Date</span>
                      <span className="font-semibold text-foreground text-sm">{new Date(detail.offerDetails.startDate).toLocaleDateString('en-IN', { dateStyle: 'long' })}</span>
                    </div>
                  )}
                  {detail.offerDetails.confirmedStipend && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-28 shrink-0 text-xs">Stipend</span>
                      <span className="font-semibold text-emerald-600 text-sm">₹{Number(detail.offerDetails.confirmedStipend).toLocaleString('en-IN')}/month</span>
                    </div>
                  )}
                  {detail.offerDetails.note && (
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground w-28 shrink-0 text-xs">Offer note</span>
                      <span className="text-muted-foreground text-xs italic">"{detail.offerDetails.note}"</span>
                    </div>
                  )}
                  {detail.offerResponse && detail.offerResponse !== 'pending' && (
                    <div className={cn('mt-2 text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1',
                      detail.offerResponse === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600')}>
                      {detail.offerResponse === 'accepted' ? '✓ Candidate accepted the offer' : '✗ Candidate declined the offer'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hired intern: task & hours tracking (pulled from their Jira board) */}
            {detail.stage === 'hired' && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-primary" /> Task &amp; Hours Tracking
                </p>
                <InternProgressPanel appId={appId} />
              </div>
            )}

            {detail.stage === 'rejected' && detail.rejectionDetails && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Rejection Details</p>
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-muted-foreground w-20 shrink-0 text-xs">Reason</span>
                    <span className="text-foreground text-xs font-medium">{detail.rejectionDetails.reason}</span>
                  </div>
                  {detail.rejectionDetails.message && (
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">Message</span>
                      <span className="text-muted-foreground text-xs italic">"{detail.rejectionDetails.message}"</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stage history */}
            {Array.isArray(detail.stageHistory) && detail.stageHistory.length > 0 && (
              <div className="p-5 border-b border-border">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Stage History</p>
                <div className="space-y-2">
                  {[...detail.stageHistory].reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0',
                        h.stage === 'hired' ? 'bg-emerald-500' : h.stage === 'rejected' ? 'bg-red-400' :
                        h.stage === 'interview' ? 'bg-violet-500' : h.stage === 'screening' ? 'bg-amber-400' : 'bg-blue-400')} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-foreground capitalize">{STAGE_LABELS[h.stage] || h.stage}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(h.changedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {h.note && <p className="text-[10px] text-muted-foreground mt-0.5 italic">"{h.note}"</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stage actions (a withdrawn application cannot be moved) */}
            <div className={`p-5 ${detail.stage === 'withdrawn' ? 'hidden' : ''}`}>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Move Pipeline Stage</p>
              <div className="flex flex-wrap gap-2">
                {STAGES.filter(s => s !== detail.stage && s !== 'withdrawn').map(s => {
                  const cfg = STAGE_CONFIG[s];
                  const Icon = cfg?.icon;
                  return (
                    <button key={s} onClick={() => setTransitionStage(s)}
                      className={cn('flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl border font-medium transition-all',
                        s === 'hired'    ? 'border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10' :
                        s === 'rejected' ? 'border-red-500/30 text-red-500 hover:bg-red-500/10' :
                        'border-border text-muted-foreground hover:border-primary/30 hover:text-primary')}>
                      {Icon && <Icon className="w-3 h-3" />}
                      {STAGE_LABELS[s]}
                    </button>
                  );
                })}
              </div>
              {['interview', 'hired', 'rejected'].includes(detail.stage) && (
                <button onClick={() => setFeedbackOpen(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl border border-amber-500/30 text-amber-600 hover:bg-amber-500/10 font-medium mt-2">
                  <Star className="w-3 h-3" /> Rate This Candidate
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stage transition dialog */}
      <StageTransitionDialog
        open={!!transitionStage}
        targetStage={transitionStage}
        app={detail}
        posting={detail?.posting_id}
        onClose={() => setTransitionStage(null)}
        onConfirm={handleStageConfirm}
      />

      {/* Peer feedback — only reachable once a real application reached interview/hired/rejected,
          enforced again server-side in createCandidateFeedback */}
      <CandidateFeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        candidate={detail ? { candidateLabel: detail.studentName || 'this candidate' } : null}
        onSubmit={async ({ rating, comment }) => {
          try {
            await submitCandidateFeedback({ studentId: detail.student_id, applicationId: appId, rating, comment });
            toast.success('Feedback saved — shown (anonymized) to other startups considering this candidate.');
          } catch (e) { toast.error(e?.message || 'Could not save feedback'); }
        }}
      />
    </>
  );
}

// ── Recommended Candidates (Wave 1) ──────────────────────────────────────────
// Masked cards only -- no name/email/phone anywhere here. "Express interest"
// sends the student an invitation to apply, it never reveals identity; any
// real contact exchange happens later through the student's own consent on
// a real application. See facade_stn_matching.js for the full reasoning.

function CandidateFeedbackDialog({ open, onClose, candidate, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  if (!candidate) return null;

  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ rating, comment }); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Rate {candidate.candidateLabel}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} type="button" onClick={() => setRating(n)}>
                <Star className={cn('w-6 h-6', n <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30')} />
              </button>
            ))}
          </div>
          <Textarea placeholder="Optional note for other startups considering this candidate…" value={comment} onChange={e => setComment(e.target.value)} rows={3} />
          <Button className="w-full" onClick={submit} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Feedback'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Hiring phase 3: request a hidden candidate's details ─────────────────────
const ACCESS_FIELDS = [
  ['contact', 'Name, email and phone'], ['college', 'College and course'], ['resume', 'Resume'],
  ['links', 'LinkedIn, GitHub and portfolio'], ['skills', 'Skills and work experience'],
];
function CandidateAccess({ posting, c, onChange }) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(ACCESS_FIELDS.map(([k]) => k));
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const a = c.access || { state: 'none' };

  const send = async () => {
    setBusy(true);
    try {
      const r = await requestCandidateAccess(posting._id, c.candidateRef, { fields, message });
      const d = r?.data ?? r;
      toast.success(d?.already === 'approved' ? 'This candidate already shares their details with you.' : `${c.candidateLabel} has been asked. Nothing is shared unless they agree.`);
      onChange({ ...a, state: d?.already === 'approved' ? 'approved' : 'pending', requestId: d?.requestId || a.requestId });
      setOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Could not send the request');
    } finally { setBusy(false); }
  };
  const openResume = async () => {
    const w = window.open('', '_blank');
    try {
      const r = await getAccessResume(a.requestId);
      const url = r?.data?.url || r?.url;
      if (!url) throw new Error('No resume link');
      if (w) w.location.href = url; else window.location.href = url;
    } catch (err) { if (w) w.close(); toast.error(err?.message || 'Could not open the resume'); }
  };

  if (a.state === 'approved' && a.details) {
    const d = a.details;
    return (
      <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs space-y-1">
        <p className="font-semibold text-emerald-700 dark:text-emerald-400">Shared with you until {a.expiresAt ? new Date(a.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</p>
        {d.name !== undefined && <p><span className="text-muted-foreground">Name:</span> {d.name} · {d.email}{d.phone ? ` · ${d.phone}` : ''}</p>}
        {d.college !== undefined && <p><span className="text-muted-foreground">College:</span> {d.college || '—'}{d.course ? ` · ${d.course}` : ''}</p>}
        {d.skills !== undefined && <p><span className="text-muted-foreground">Skills:</span> {(d.skills || []).join(', ') || '—'}</p>}
        {d.experience?.length > 0 && <p><span className="text-muted-foreground">Experience:</span> {d.experience.map(x => [x.role, x.company].filter(Boolean).join(' at ')).join('; ')}</p>}
        {d.linkedin !== undefined && <p className="space-x-2">{d.linkedin && <a className="text-primary underline" href={d.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>}{d.github && <a className="text-primary underline" href={d.github} target="_blank" rel="noopener noreferrer">GitHub</a>}{d.portfolio && <a className="text-primary underline" href={d.portfolio} target="_blank" rel="noopener noreferrer">Portfolio</a>}</p>}
        {d.resumeAvailable && <button type="button" className="text-primary underline" onClick={openResume}>Open resume</button>}
      </div>
    );
  }
  const note = { pending: 'Details requested — waiting for the candidate.', denied: 'The candidate declined to share their details for this role.', revoked: 'The candidate stopped sharing their details.', expired: 'Their sharing period ended.' }[a.state];
  if (note) return <p className="mt-2 text-xs text-muted-foreground">{note}</p>;
  if (!open) return <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 mt-2" onClick={() => setOpen(true)}><Lock className="w-3.5 h-3.5" /> Request details</Button>;
  return (
    <div className="mt-2 rounded-lg border border-border p-3 space-y-2 text-xs">
      <p className="font-semibold">Ask {c.candidateLabel} to share:</p>
      {ACCESS_FIELDS.map(([k, label]) => (
        <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={fields.includes(k)} onChange={e => setFields(f => e.target.checked ? [...f, k] : f.filter(x => x !== k))} /> {label}</label>
      ))}
      <textarea className="w-full rounded border border-border bg-background p-2" rows={2} maxLength={500} placeholder="Optional note to the candidate (why you're interested)" value={message} onChange={e => setMessage(e.target.value)} />
      <p className="text-muted-foreground">They decide what to share. If they agree, you see it for 90 days.</p>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs" disabled={busy || !fields.length} onClick={send}>{busy ? 'Sending…' : 'Send request'}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function RecommendedCandidatesPanel({ posting, onClose }) {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [reveals, setReveals] = useState(null); // { used, limit, remaining }

  const load = useCallback(() => {
    if (!posting?._id) return;
    setLoading(true);
    getRecommendedCandidates(posting._id)
      .then(res => { setCandidates(res?.candidates || []); setReveals(res?.reveals || null); })
      .catch(() => toast.error('Could not load recommended candidates'))
      .finally(() => setLoading(false));
  }, [posting?._id]);

  useEffect(() => { load(); }, [load]);

  const interest = async (c) => {
    try {
      const r = await expressCandidateInterest(posting._id, c.candidateRef);
      // Someone at your startup already did this for this posting: the student is not messaged twice and no
      // reveal is used.
      const already = r?.alreadySent || r?.data?.alreadySent;
      if (already) toast.info(`${c.candidateLabel} was already notified about this role.`);
      else toast.success(`${c.candidateLabel} has been notified — no contact details are shared until they apply.`);
      setCandidates(prev => prev.map(x => x.candidateRef === c.candidateRef ? { ...x, interestExpressed: true } : x));
      if (!already) setReveals(prev => prev && prev.limit !== -1 ? { ...prev, used: prev.used + 1, remaining: Math.max(prev.remaining - 1, 0) } : prev);
    } catch (e) {
      // 402 = free-reveal limit hit for this posting — backend's message
      // already names the limit and what to do next, show it as-is.
      toast.error(e?.message || 'Could not send interest');
    }
  };

  return (
    <Dialog open={!!posting} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-500" /> Recommended Candidates — {posting?.title}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2 mb-1">
          Matched by skills and resume content. Identity stays masked until you express interest and the candidate applies.
        </p>
        {reveals && (
          <p className="text-xs -mt-1 mb-2 font-medium">
            {reveals.limit === -1
              ? <span className="text-muted-foreground">Unlimited candidate reveals on this posting.</span>
              : reveals.remaining > 0
                ? <span className="text-muted-foreground">{reveals.remaining} of {reveals.limit} free candidate reveals left on this posting.</span>
                : <span className="text-destructive">You've used all {reveals.limit} free reveals for this posting — contact your account manager to unlock more.</span>}
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No strong matches yet — check back once more students have resumes on file.</p>
        ) : (
          <div className="space-y-3">
            {candidates.map(c => (
              <div key={c.candidateRef} className="border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="font-semibold text-sm text-foreground">{c.candidateLabel}</p>
                    {c.private && <p className="text-[11px] text-muted-foreground mt-0.5">Private profile: only the match is shown</p>}
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 shrink-0">
                    {c.matchPct ?? c.skillMatchPct}% match
                  </span>
                </div>
                {/* Before a student agrees, a startup sees only the match and their last NexusAI training and batch. */}
                {c.lastTraining && (
                  <div className="text-xs text-muted-foreground mb-2 space-y-0.5">
                    <p className="flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Last trained with NexusAI: <span className="text-foreground font-medium">{c.lastTraining.program || 'Programme'}</span>
                      {c.lastTraining.at && <span> · {new Date(c.lastTraining.at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>}
                      {c.lastTraining.status && <span> · {c.lastTraining.status}</span>}</p>
                    {c.lastTraining.batch && <p className="pl-4">Cohort / batch: <span className="text-foreground">{c.lastTraining.batch}</span></p>}
                  </div>
                )}
                {!c.private && !c.lastTraining && <p className="text-xs text-muted-foreground mb-2">No NexusAI training on record yet.</p>}
                <Button size="sm" variant={c.interestExpressed ? 'ghost' : 'outline'} disabled={c.interestExpressed}
                  className="h-7 text-xs gap-1.5" onClick={() => interest(c)}>
                  {c.interestExpressed ? <><ThumbsUp className="w-3.5 h-3.5" /> Interest Sent</> : <><Send className="w-3.5 h-3.5" /> Express Interest</>}
                </Button>
                <CandidateAccess posting={posting} c={c}
                  onChange={(access) => setCandidates(prev => prev.map(x => x.candidateRef === c.candidateRef ? { ...x, access } : x))} />
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function InternHiring() {
  const [postings, setPostings]           = useState([]);
  const [postingsLoading, setPostingsLoading] = useState(false);

  const [applications, setApplications]       = useState([]);
  const [appsLoading, setAppsLoading]         = useState(false);
  const [selectedPostingFilter, setSelectedPostingFilter] = useState('');
  const [matchScores, setMatchScores] = useState(null); // { [applicationId]: { matchPct, unpaidFit } } or null if not yet run
  const [matchScoring, setMatchScoring] = useState(false);

  // Offline center from startup profile
  const [offlineCenters, setOfflineCenters] = useState([]);
  const [centerLoading, setCenterLoading] = useState(false);

  const [formOpen, setFormOpen]           = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [candidatesPosting, setCandidatesPosting] = useState(null); // posting whose recommended-candidates panel is open
  const [registerCenterOpen, setRegisterCenterOpen] = useState(false);
  const [registerCenterForceEdit, setRegisterCenterForceEdit] = useState(false);

  const [search, setSearch]         = useState('');
  const [activeTab, setActiveTab]   = useState('postings');
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [openStage, setOpenStage] = useState(null); // stage dialog to open with the application (Kanban quick move)
  const [appStageFilter, setAppStageFilter] = useState('');

  // ── Load startup profile to get offline center ──────────────────────────────
  const loadStartupProfile = useCallback(async () => {
    try {
      setCenterLoading(true);
      const res = await getMyStartup();
      const startup = res?.startup ?? res;
      setOfflineCenters(startup?.offlineCenters ?? []);
    } catch {
      // non-blocking — just no center data
    } finally {
      setCenterLoading(false);
    }
  }, []);

  const loadPostings = useCallback(async () => {
    try {
      setPostingsLoading(true);
      const res = await listMyPostings();
      const list = res?.data ?? res;
      setPostings(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Failed to load postings');
    } finally {
      setPostingsLoading(false);
    }
  }, []);

  const loadApplications = useCallback(async (postingId = '') => {
    try {
      setAppsLoading(true);
      const res = await listApplications(postingId ? { postingId } : {});
      const list = res?.data ?? res;
      setApplications(Array.isArray(list) ? list : []);
      setMatchScores(null); // scores are posting-specific — stale once the list changes
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setAppsLoading(false);
    }
  }, []);

  // Run the matching engine over this posting's real applicants (not the
  // candidate pool — same scoring, different data source). Requires one
  // specific posting selected, since matching needs that posting's embedding.
  const runApplicationMatching = useCallback(async () => {
    if (!selectedPostingFilter) { toast.error('Select a specific posting first'); return; }
    setMatchScoring(true);
    try {
      const res = await getApplicationMatchScores(selectedPostingFilter);
      const byAppId = {};
      (res?.applications || []).forEach(a => { byAppId[a.applicationId] = a; });
      setMatchScores(byAppId);
      toast.success('Applications ranked by resume match');
    } catch (e) {
      toast.error(e?.message || 'Could not run matching');
    } finally {
      setMatchScoring(false);
    }
  }, [selectedPostingFilter]);

  useEffect(() => {
    loadStartupProfile();
    loadPostings();
    loadApplications(); // load upfront so count shows in tab badge
  }, [loadStartupProfile, loadPostings, loadApplications]);

  useEffect(() => {
    if (activeTab === 'pipeline' || activeTab === 'applications') loadApplications(selectedPostingFilter);
  }, [activeTab, selectedPostingFilter, loadApplications]);

  const handleToggleStatus = async (p) => {
    const newStatus = p.status === 'active' ? 'paused' : 'active';
    try {
      await updatePosting(p._id, { status: newStatus });
      toast.success(`Posting ${newStatus === 'active' ? 'published' : 'paused'}`);
      loadPostings();
    } catch (err) {
      toast.error(err?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this posting? This cannot be undone.')) return;
    try {
      await deletePosting(id);
      toast.success('Posting deleted');
      loadPostings();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete');
    }
  };

  const handleMoveStage = async (appId, newStage) => {
    // Interview needs a date and hire needs offer details: open the application with that dialog instead of
    // moving blindly (which used to email the student "interview: TBD").
    if (newStage === 'interview' || newStage === 'hired') {
      setOpenStage(newStage);
      setSelectedAppId(appId);
      return;
    }
    try {
      await updateApplicationStage(appId, { stage: newStage });
      toast.success(`Moved to ${STAGE_LABELS[newStage]}`);
      loadApplications(selectedPostingFilter);
    } catch (err) {
      toast.error(err?.message || 'Failed to move');
    }
  };

  const kanbanByStage = STAGES.reduce((acc, s) => {
    acc[s] = applications.filter(a => a.stage === s);
    return acc;
  }, {});

  const filteredPostings = postings.filter(p =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.domain?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (s) => {
    if (s === 'active')  return 'text-green-700 bg-green-50 border-green-200';
    if (s === 'paused')  return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    if (s === 'closed')  return 'text-red-700 bg-red-50 border-red-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  // Computed stats
  const stats = {
    totalApps:   applications.length,
    newApps:     applications.filter(a => a.stage === 'applied').length,
    inProgress:  applications.filter(a => ['screening','interview'].includes(a.stage)).length,
    hired:       applications.filter(a => a.stage === 'hired').length,
    activePostings: postings.filter(p => p.status === 'active').length,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Intern Hiring</h1>
          <p className="text-muted-foreground text-sm mt-1">Post internships, manage your pipeline, and hire top talent</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Offline center status pill in header — clickable to open review dialog */}
          {!centerLoading && offlineCenters.length > 0 ? (
            <button
              onClick={() => setRegisterCenterOpen(true)}
              className="flex items-center gap-0"
            >
              <CenterStatusBadge offlineCenter={offlineCenters[0]} />
              {offlineCenters.length > 1 && (
                <span className="ml-1.5 text-[10px] text-muted-foreground">+{offlineCenters.length - 1} more</span>
              )}
            </button>
          ) : !centerLoading && (
            <button
              onClick={() => setRegisterCenterOpen(true)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground border border-dashed border-border px-3 py-1.5 rounded-full hover:border-primary/50 hover:text-primary transition-colors"
            >
              <Building2 className="w-3.5 h-3.5" /> Register offline center
            </button>
          )}
          <Button
            className="gradient-primary text-primary-foreground border-0 gap-2"
            onClick={() => { setEditingPosting(null); setFormOpen(true); }}
          >
            <Plus className="w-4 h-4" /> Post Internship
          </Button>
        </div>
      </div>

      {/* ── Quick Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Active Postings', val: stats.activePostings, icon: Briefcase,    color: 'text-primary',    bg: 'bg-primary/5',    tab: 'postings' },
          { label: 'New Applications', val: stats.newApps,       icon: Users,        color: 'text-blue-600',   bg: 'bg-blue-50',     tab: 'applications', stage: 'applied' },
          { label: 'In Progress',      val: stats.inProgress,    icon: RefreshCw,    color: 'text-amber-600',  bg: 'bg-amber-50',    tab: 'applications', stage: 'screening' },
          { label: 'Hired',            val: stats.hired,         icon: CheckCircle2, color: 'text-emerald-600',bg: 'bg-emerald-50',  tab: 'applications', stage: 'hired' },
          { label: 'Total Applications',val: stats.totalApps,   icon: Award,        color: 'text-foreground', bg: 'bg-muted/40',    tab: 'applications' },
        ].map(s => (
          <button key={s.label} onClick={() => { if (s.stage) setAppStageFilter(s.stage); setActiveTab(s.tab); }}
            className={cn('flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all text-left', s.bg)}>
            <s.icon className={cn('w-4 h-4 shrink-0', s.color)} />
            <div>
              <p className="text-xl font-black text-foreground leading-none">{s.val}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          </button>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted">
          <TabsTrigger value="postings">
            Postings {postings.length > 0 && <span className="ml-1.5 text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{postings.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="applications" className="relative">
            Applications
            {applications.length > 0 && (
              <span className="ml-1.5 text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                {applications.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pipeline">
            Kanban {applications.length > 0 && <span className="ml-1 text-[10px] font-bold bg-muted-foreground/20 px-1.5 py-0.5 rounded-full">{applications.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="pool">Intern Pool</TabsTrigger>
          <TabsTrigger value="tci">TCI Programs</TabsTrigger>
        </TabsList>

        {/* ── Postings Tab ── */}
        <TabsContent value="postings" className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search postings..."
                className="pl-9"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon" onClick={loadPostings} disabled={postingsLoading}>
              <RefreshCw className={`w-4 h-4 ${postingsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {postingsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPostings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border rounded-xl">
              <Briefcase className="w-10 h-10 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground font-medium">No postings yet</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first internship posting to start finding talent</p>
              <Button
                size="sm"
                className="gradient-primary text-primary-foreground border-0 gap-2"
                onClick={() => { setEditingPosting(null); setFormOpen(true); }}
              >
                <Plus className="w-4 h-4" /> Post Internship
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPostings.map(p => {
                const dl = p.applicationDeadline
                  ? Math.ceil((new Date(p.applicationDeadline) - Date.now()) / 86400000)
                  : null;
                const dlText = dl === null ? null : dl < 0 ? 'Closed' : dl === 0 ? 'Last day!' : `${dl}d left`;
                const dlUrgent = dl !== null && dl <= 3;

                const stipendLabel = !p.isPaid || p.payType === 'free' || !p.payType
                  ? 'Unpaid / Free'
                  : p.payType === 'weekly'
                    ? `₹${Number(p.stipend || 0).toLocaleString('en-IN')}/week${p.payHours ? ` · ${p.payHours}h/wk` : ''}`
                    : p.payType === 'hourly'
                      ? `₹${Number(p.stipend || 0).toLocaleString('en-IN')}/hr${p.payHours ? ` · ${p.payHours}h/wk` : ''}`
                      : `₹${Number(p.stipend || 0).toLocaleString('en-IN')}/mo${p.payMonths ? ` × ${p.payMonths} mo` : ''}`;

                return (
                  <Card key={p._id} className={cn('border-border transition-all hover:shadow-md',
                    p.status === 'active' ? 'border-l-4 border-l-emerald-500/60' :
                    p.status === 'paused' ? 'border-l-4 border-l-amber-400/60' :
                    p.status === 'draft'  ? 'border-l-4 border-l-border' :
                    'border-l-4 border-l-muted-foreground/30')}>
                    <CardContent className="p-5">

                      {/* ── Top row: title + actions ── */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-bold text-foreground text-base leading-tight">{p.title}</h3>
                            {/* Status */}
                            <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize', statusBadge(p.status))}>
                              {p.status}
                            </span>
                            {/* Feature badges */}
                            {p.ppoOffered        && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">PPO</span>}
                            {p.certificateOffered && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Certificate</span>}
                            {p.aiRole?.enabled   && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">🤖 AI Role</span>}
                            {p.poc?.required     && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">📋 POC</span>}
                          </div>
                          {p.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{p.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => handleToggleStatus(p)}
                            title={p.status === 'active' ? 'Pause' : 'Publish'}>
                            {p.status === 'active'
                              ? <PauseCircle className="w-4 h-4 text-amber-500" />
                              : <PlayCircle  className="w-4 h-4 text-emerald-600" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => { setEditingPosting(p); setFormOpen(true); }}>
                            <Edit3 className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete(p._id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* ── Stats grid ── */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                        {[
                          { icon: Briefcase,    val: p.domain,    label: 'Domain' },
                          { icon: MapPin,       val: `${p.workType}${p.location ? ` · ${p.location}` : ''}`, label: 'Work type', capitalize: true },
                          { icon: Clock,        val: p.duration,  label: 'Duration' },
                          { icon: DollarSign,   val: stipendLabel, label: 'Compensation', colored: p.isPaid },
                        ].map(s => (
                          <div key={s.label} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 border border-border/60">
                            <s.icon className={cn('w-3.5 h-3.5 shrink-0', s.colored ? 'text-emerald-600' : 'text-muted-foreground')} />
                            <div className="min-w-0">
                              <p className="text-[9px] text-muted-foreground uppercase tracking-wide leading-none mb-0.5">{s.label}</p>
                              <p className={cn('text-xs font-semibold truncate', s.capitalize && 'capitalize', s.colored && 'text-emerald-600 dark:text-emerald-400')}>{s.val || '—'}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* ── Second row of stats ── */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          <strong className="text-foreground">{p.applicationsCount || 0}</strong> application{(p.applicationsCount || 0) !== 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <GraduationCap className="w-3.5 h-3.5" />
                          <strong className="text-foreground">{p.openings || 1}</strong> opening{(p.openings || 1) !== 1 ? 's' : ''}
                        </span>
                        {p.startDate && (
                          <span className="flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5" />
                            Starts {new Date(p.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                        {dlText && (
                          <span className={cn('flex items-center gap-1.5 font-medium', dlUrgent ? 'text-red-500' : '')}>
                            <AlertTriangle className={cn('w-3.5 h-3.5', dlUrgent ? 'text-red-500' : 'text-muted-foreground')} />
                            {dlText === 'Closed' ? 'Applications closed' : `Deadline: ${new Date(p.applicationDeadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} (${dlText})`}
                          </span>
                        )}
                      </div>

                      {/* ── Skills ── */}
                      {p.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {p.skills.slice(0, 6).map(s => (
                            <span key={s} className="text-[10px] px-2 py-0.5 rounded bg-primary/5 text-primary border border-primary/15 font-mono">{s}</span>
                          ))}
                          {p.skills.length > 6 && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-muted text-muted-foreground">+{p.skills.length - 6} more</span>
                          )}
                        </div>
                      )}

                      {/* ── Eligibility chips ── */}
                      {(p.eligibility?.minCgpa > 0 || p.eligibility?.branches?.length > 0 || p.eligibility?.yearOfStudy?.length > 0) && (
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">Eligibility:</span>
                          {p.eligibility.minCgpa > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">CGPA ≥ {p.eligibility.minCgpa}</span>
                          )}
                          {p.eligibility.yearOfStudy?.slice(0, 3).map(y => (
                            <span key={y} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{y}</span>
                          ))}
                          {p.eligibility.branches?.length > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
                              {p.eligibility.branches.length} branch{p.eligibility.branches.length !== 1 ? 'es' : ''}
                            </span>
                          )}
                        </div>
                      )}

                      {/* ── AI Role summary ── */}
                      {p.aiRole?.enabled && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-border/50">
                          <span className="text-[9px] font-bold text-violet-500 uppercase tracking-wide">AI Tools:</span>
                          {p.aiRole.tools?.slice(0, 4).map(t => (
                            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20">{t}</span>
                          ))}
                          {(p.aiRole.tools?.length || 0) > 4 && (
                            <span className="text-[10px] text-muted-foreground">+{p.aiRole.tools.length - 4} more</span>
                          )}
                          {p.aiRole.promptEngLevel && (
                            <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-violet-300 text-violet-600 dark:border-violet-500/30 dark:text-violet-400">
                              {p.aiRole.promptEngLevel}
                            </span>
                          )}
                        </div>
                      )}

                      {/* ── POC task summary ── */}
                      {p.poc?.required && p.poc?.taskTitle && (
                        <div className="flex items-start gap-2 pt-2 border-t border-border/50">
                          <BookOpen className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <span className="text-[9px] font-bold text-amber-600 uppercase tracking-wide">Assignment: </span>
                            <span className="text-[10px] text-muted-foreground">{p.poc.taskTitle}</span>
                            {p.poc.formats?.length > 0 && (
                              <span className="text-[10px] text-muted-foreground ml-1">· {p.poc.formats.join(', ')}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ── View applications CTA ── */}
                      <div className="flex items-center justify-between pt-3 mt-1 border-t border-border/50">
                        <span className="text-xs text-muted-foreground">
                          {p.applicationsCount > 0
                            ? <><strong className="text-foreground">{p.applicationsCount}</strong> application{p.applicationsCount !== 1 ? 's' : ''} received</>
                            : 'No applications yet'}
                        </span>
                        <div className="flex items-center gap-2">
                          {p.status === 'active' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 border-violet-300 text-violet-600 hover:bg-violet-50 dark:border-violet-500/30 dark:text-violet-400"
                              onClick={() => setCandidatesPosting(p)}>
                              <Sparkles className="w-3.5 h-3.5" /> Recommended Candidates
                            </Button>
                          )}
                          {p.applicationsCount > 0 && (
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/5"
                              onClick={() => {
                                setSelectedPostingFilter(p._id);
                                setActiveTab('applications');
                                loadApplications(p._id);
                              }}>
                              <Users className="w-3.5 h-3.5" /> View Applications
                            </Button>
                          )}
                        </div>
                      </div>

                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Intern Pool Tab ── */}
        <TabsContent value="pool" className="mt-6">
          <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border rounded-xl">
            <Brain className="w-12 h-12 text-muted-foreground/40 mb-4" />
            <p className="font-medium text-muted-foreground">AI Intern Pool</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              Browse pre-vetted InternNexus students matched to your requirements. Available on Pro plan.
            </p>
            <Button variant="outline" size="sm" className="mt-4 gap-2">
              <Zap className="w-3.5 h-3.5" /> Upgrade to Pro
            </Button>
          </div>
        </TabsContent>

        {/* ── Applications Tab ── */}
        <TabsContent value="applications" className="mt-6">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <Select
              value={selectedPostingFilter || '__all__'}
              onValueChange={v => { const val = v === '__all__' ? '' : v; setSelectedPostingFilter(val); loadApplications(val); }}
            >
              <SelectTrigger className="w-56 h-9 text-sm">
                <SelectValue placeholder="All postings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All postings</SelectItem>
                {postings.map(p => (
                  <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Stage filter pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {['all', ...STAGES.filter(s => s !== 'withdrawn')].map(s => {
                const count = s === 'all' ? applications.length : applications.filter(a => a.stage === s).length;
                const isActive = (appStageFilter || 'all') === s;
                return (
                  <button key={s} onClick={() => setAppStageFilter(s === 'all' ? '' : s)}
                    className={cn('text-xs px-3 py-1.5 rounded-full border font-medium transition-all capitalize flex items-center gap-1',
                      isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/30')}>
                    {s === 'all' ? 'All' : STAGE_LABELS[s]}
                    <span className={cn('text-[10px] font-bold', isActive ? 'opacity-80' : 'opacity-60')}>({count})</span>
                  </button>
                );
              })}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {selectedPostingFilter && (
                <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={runApplicationMatching} disabled={matchScoring}>
                  {matchScoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-violet-500" />}
                  Run Recommendation Engine
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => loadApplications(selectedPostingFilter)} disabled={appsLoading}>
                <RefreshCw className={cn('w-3.5 h-3.5', appsLoading && 'animate-spin')} /> Refresh
              </Button>
            </div>
          </div>

          {appsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-7 h-7 animate-spin text-primary" />
            </div>
          ) : (() => {
            let filtered = applications.filter(a => !appStageFilter || a.stage === appStageFilter);
            if (matchScores) {
              filtered = [...filtered].sort((a, b) => (matchScores[b._id]?.matchPct ?? -1) - (matchScores[a._id]?.matchPct ?? -1));
            }
            if (!filtered.length) return (
              <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-border rounded-xl">
                <Users className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="font-semibold text-muted-foreground">No applications yet</p>
                <p className="text-sm text-muted-foreground mt-1">Applications from InternNexus students will appear here</p>
              </div>
            );
            return (
              <div className="space-y-2">
                {filtered.map(app => {
                  const sm = STAGE_COLORS[app.stage] || '';
                  const posting = app.posting_id;
                  const daysAgo = Math.floor((Date.now() - new Date(app.createdAt)) / 86400000);

                  return (
                    <div key={app._id}
                      onClick={() => { setOpenStage(null); setSelectedAppId(app._id); }}
                      className="group flex items-center gap-4 p-4 rounded-xl border border-border bg-background hover:bg-muted/30 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer">

                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                        {(app.studentName?.[0] || '?').toUpperCase()}
                      </div>

                      {/* Student info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">{app.studentName || 'Applicant'}</p>
                          {app.studentCgpa > 0 && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                              CGPA {app.studentCgpa}
                            </span>
                          )}
                          {app.poc?.submittedAt && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20">
                              📋 POC Submitted
                            </span>
                          )}
                          {app.aiEvaluation?.evaluatedAt && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5" /> {app.aiEvaluation.overallScore}/100
                            </span>
                          )}
                          {matchScores?.[app._id] && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20 flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5" /> {matchScores[app._id].matchPct}% resume match
                              {matchScores[app._id].unpaidFit && ' · unpaid-fit'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-xs text-muted-foreground truncate">{app.studentCollege || app.studentEmail}</p>
                          {posting?.title && (
                            <span className="text-[10px] text-muted-foreground/60">· {posting.title}</span>
                          )}
                        </div>
                      </div>

                      {/* Stage badge */}
                      <div className="shrink-0 text-right space-y-1">
                        <span className={cn('inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border', sm)}>
                          <span className={cn('w-1.5 h-1.5 rounded-full',
                            app.stage === 'applied' ? 'bg-blue-500' : app.stage === 'screening' ? 'bg-amber-400' :
                            app.stage === 'interview' ? 'bg-violet-500' : app.stage === 'hired' ? 'bg-emerald-500' : 'bg-red-400')} />
                          {STAGE_LABELS[app.stage]}
                        </span>
                        <p className="text-[10px] text-muted-foreground">
                          {daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`}
                        </p>
                      </div>

                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary shrink-0 transition-colors" />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>

        {/* ── Kanban Pipeline Tab ── */}
        <TabsContent value="pipeline" className="mt-6">
          <div className="flex items-center gap-3 mb-5">
            <Select
              value={selectedPostingFilter || '__all__'}
              onValueChange={v => setSelectedPostingFilter(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="All postings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All postings</SelectItem>
                {postings.map(p => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => loadApplications(selectedPostingFilter)} disabled={appsLoading}>
              <RefreshCw className={`w-4 h-4 ${appsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {appsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {STAGES.filter(s => s !== 'withdrawn').map(stage => (
                <div key={stage} className="kanban-col min-w-60 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <span className={cn('text-xs font-semibold px-2 py-1 rounded-full border', STAGE_COLORS[stage])}>
                      {STAGE_LABELS[stage]}
                    </span>
                    <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {kanbanByStage[stage]?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {kanbanByStage[stage]?.map(app => (
                      <KanbanCard key={app._id} app={app} currentStage={stage} onMove={handleMoveStage} onViewDetail={(id) => { setOpenStage(null); setSelectedAppId(id); }} />
                    ))}
                    {!kanbanByStage[stage]?.length && (
                      <div className="text-center py-8 text-xs text-muted-foreground border border-dashed border-border rounded-lg">
                        No candidates
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── TCI Programs Tab ── */}
        <TabsContent value="tci" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Tech Excellence Track</h3>
                    <p className="text-xs text-muted-foreground">6 interns • Active</p>
                  </div>
                  <span className="ml-auto text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full border border-green-200">Active</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">Week 4/12</span><span className="font-medium">33%</span></div>
                  <div className="w-full h-2 bg-muted rounded-full"><div className="h-full w-1/3 bg-primary rounded-full" /></div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1 text-xs">View Progress</Button>
                  <Button size="sm" className="flex-1 gradient-primary text-primary-foreground border-0 text-xs">Issue PPO</Button>
                </div>
              </CardContent>
            </Card>
            <Card className="border-dashed border-border flex items-center justify-center min-h-36">
              <CardContent className="text-center p-6">
                <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Create New TCI Program</p>
                <Button size="sm" variant="outline">New Program</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Posting Form Dialog */}
      <PostingFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingPosting(null); }}
        onSaved={loadPostings}
        editing={editingPosting}
        offlineCenters={offlineCenters}
        onNeedRegister={(openInEdit = false) => {
          setFormOpen(false);
          setRegisterCenterForceEdit(openInEdit);
          setRegisterCenterOpen(true);
        }}
      />

      {/* Register / Review Offline Center Dialog */}
      <RegisterCenterDialog
        open={registerCenterOpen}
        onClose={() => { setRegisterCenterOpen(false); setRegisterCenterForceEdit(false); }}
        offlineCenters={offlineCenters}
        forceEdit={registerCenterForceEdit}
        onRegistered={(centers) => {
          const arr = Array.isArray(centers) ? centers : [centers];
          const hadNone = offlineCenters.length === 0;
          setOfflineCenters(arr);
          if (hadNone) setTimeout(() => setFormOpen(true), 300);
        }}
      />

      {/* Application detail panel */}
      {selectedAppId && (
        <ApplicationDetailPanel
          appId={selectedAppId}
          openStage={openStage}
          onClose={() => { setSelectedAppId(null); setOpenStage(null); }}
          onStageChange={(id, stage) => {
            setApplications(prev => prev.map(a => a._id === id ? { ...a, stage } : a));
          }}
        />
      )}

      {/* Recommended Candidates panel (Wave 1) */}
      {candidatesPosting && (
        <RecommendedCandidatesPanel
          posting={candidatesPosting}
          onClose={() => setCandidatesPosting(null)}
        />
      )}
    </div>
  );
}
